import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../config/firebase';
import { collection, getDocs, doc, updateDoc, getDoc, setDoc, serverTimestamp, query, orderBy, limit, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAnalyticsSummary } from '../services/analyticsService';
import DOMPurify from 'dompurify';

export default function Admin() {
  const { user, userProfile } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState('analytics');

  // User management state
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [updatingUserId, setUpdatingUserId] = useState(null);

  // Pricing management state
  const [pricingConfig, setPricingConfig] = useState(null);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [savingPricing, setSavingPricing] = useState(false);

  // Team tier modal state
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [teamModalUser, setTeamModalUser] = useState(null);
  const [teamSeats, setTeamSeats] = useState(5);

  // Delete user modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteModalUser, setDeleteModalUser] = useState(null);
  const [deletingUserId, setDeletingUserId] = useState(null);

  // User profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileModalUser, setProfileModalUser] = useState(null);

  // Advanced filters state
  const [tierFilter, setTierFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [authProviderFilter, setAuthProviderFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');

  // Bulk selection state
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);

  // Terms management state
  const [termsConfig, setTermsConfig] = useState(null);
  const [termsLoading, setTermsLoading] = useState(false);
  const [savingTerms, setSavingTerms] = useState(false);
  const [termsVersions, setTermsVersions] = useState([]);
  const [showNewVersionModal, setShowNewVersionModal] = useState(false);
  const [termsDataSeeded, setTermsDataSeeded] = useState(true);
  const [termsPreview, setTermsPreview] = useState(null);
  const [showTermsPreview, setShowTermsPreview] = useState(false);
  const [newVersion, setNewVersion] = useState({
    version: '',
    type: 'terms',
    title: '',
    content: '',
    summary: ''
  });
  const [acceptanceStats, setAcceptanceStats] = useState({ total: 0, recent: 0 });

  // Platform Analytics state
  const [platformStats, setPlatformStats] = useState(null);
  const [meetingStats, setMeetingStats] = useState(null);
  const [siteAnalytics, setSiteAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsError, setAnalyticsError] = useState(null);

  // Cookie Consent Analytics state
  const [consentStats, setConsentStats] = useState(null);

  // Waitlist state
  const [waitlistEntries, setWaitlistEntries] = useState([]);
  const [waitlistLoading, setWaitlistLoading] = useState(false);
  const [waitlistStats, setWaitlistStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, thisWeek: 0 });
  const [waitlistSearch, setWaitlistSearch] = useState('');
  const [waitlistFilter, setWaitlistFilter] = useState('all');

  // Email management state
  const [emailStats, setEmailStats] = useState(null);
  const [emailConfig, setEmailConfig] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLoading, setEmailLoading] = useState(false);
  const [showCampaignModal, setShowCampaignModal] = useState(false);
  const [showTestEmailModal, setShowTestEmailModal] = useState(false);
  const [campaignData, setCampaignData] = useState({ name: '', subject: '', html: '', audience: 'all' });
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);

  // New email features
  const [emailUsage, setEmailUsage] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [showEmailEditor, setShowEmailEditor] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [sendEmailData, setSendEmailData] = useState({
    templateId: '',
    audience: 'all',
    recipients: '',
    customSubject: '',
    variables: {}
  });
  const [emailSubTab, setEmailSubTab] = useState('overview');

  // Database migration state
  const [migrationRunning, setMigrationRunning] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [migrationLogs, setMigrationLogs] = useState([]);
  const [emailEditorData, setEmailEditorData] = useState({
    subject: '',
    previewMode: false,
    blocks: [
      { id: '1', type: 'heading', content: 'Your Email Title Here' },
      { id: '2', type: 'text', content: 'Hi {{firstName}},' },
      { id: '3', type: 'text', content: 'Write your email content here...' },
      { id: '4', type: 'button', content: 'Click Here', url: 'https://quickmeet.cam', color: '#0066cc' }
    ]
  });

  const isAdmin = userProfile?.isAdmin === true;

  useEffect(() => {
    if (!isAdmin && userProfile) {
      toast.error('Access denied - Admin only');
      navigate('/dashboard');
      return;
    }

    if (isAdmin) {
      loadUsers();
      loadPricingConfig();
      loadTermsConfig();
      loadPlatformAnalytics();
      loadWaitlist();
      loadEmailData();
    }
  }, [isAdmin, userProfile]);

  const loadUsers = async () => {
    setIsLoading(true);
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);

      const usersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      usersList.sort((a, b) => {
        const dateA = a.createdAt?.toMillis?.() || 0;
        const dateB = b.createdAt?.toMillis?.() || 0;
        return dateB - dateA;
      });

      setUsers(usersList);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserTier = async (userId, newTier, seats = null) => {
    setUpdatingUserId(userId);
    try {
      const userRef = doc(db, 'users', userId);
      const subscriptionStatus = newTier === 'pro' || newTier === 'team' ? 'active' : 'inactive';
      const isPremium = newTier !== 'free';

      const updateData = {
        subscriptionTier: newTier,
        subscriptionStatus: subscriptionStatus,
        isPremium: isPremium,
        testMode: true,
        testModeSetAt: new Date().toISOString(),
        testModeSetBy: user.email
      };

      if (newTier === 'team') {
        updateData.teamSeats = seats || 5;
        updateData.teamSeatsUsed = 1;
      } else {
        updateData.teamSeats = null;
        updateData.teamSeatsUsed = null;
      }

      await updateDoc(userRef, updateData);

      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === userId
            ? { ...u, subscriptionTier: newTier, subscriptionStatus, isPremium, testMode: true, teamSeats: newTier === 'team' ? (seats || 5) : null }
            : u
        )
      );

      toast.success(`Updated to ${(newTier || 'unknown').toUpperCase()}${newTier === 'team' ? ` (${seats || 5} seats)` : ''}`);
    } catch (error) {
      console.error('Error updating user tier:', error);
      toast.error('Failed to update subscription');
    } finally {
      setUpdatingUserId(null);
      setShowTeamModal(false);
      setTeamModalUser(null);
      setTeamSeats(5);
    }
  };

  const handleTeamTierClick = (userId) => {
    const targetUser = users.find(u => u.id === userId);
    setTeamModalUser(targetUser);
    setTeamSeats(targetUser?.teamSeats || 5);
    setShowTeamModal(true);
  };

  const handleDeleteClick = (userId) => {
    const targetUser = users.find(u => u.id === userId);
    setDeleteModalUser(targetUser);
    setShowDeleteModal(true);
  };

  const deleteUser = async (userId, action = 'disable') => {
    setDeletingUserId(userId);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) throw new Error('Not authenticated');

      const response = await fetch('/api/admin', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'manage-user', userId, userAction: action })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update user');
      }

      // Update local state
      if (action === 'delete') {
        // Permanent delete - remove user from list entirely
        setUsers(prevUsers => prevUsers.filter(u => u.id !== userId));
        toast.success(data.message);
      } else if (action === 'disable') {
        setUsers(prevUsers => prevUsers.map(u =>
          u.id === userId ? { ...u, disabled: true } : u
        ));
        toast.success(data.message);
      } else if (action === 'enable') {
        setUsers(prevUsers => prevUsers.map(u =>
          u.id === userId ? { ...u, disabled: false } : u
        ));
        toast.success(data.message);
      }

      setShowDeleteModal(false);
      setDeleteModalUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.message);
    } finally {
      setDeletingUserId(null);
    }
  };

  const loadPricingConfig = async () => {
    setPricingLoading(true);
    try {
      const configRef = doc(db, 'siteConfig', 'pricing');
      const configSnap = await getDoc(configRef);

      if (configSnap.exists()) {
        setPricingConfig(configSnap.data());
      } else {
        const defaultConfig = {
          free: { name: 'Free', price: '$0', period: 'forever', features: [] },
          pro: { name: 'Pro', price: '$15', period: 'per user/month', stripePriceId: '', features: [] },
          team: { name: 'Team', price: '$20', period: 'per seat/month', minSeats: 5, maxSeats: 1000, seatPrice: 20, features: [] }
        };
        await setDoc(configRef, defaultConfig);
        setPricingConfig(defaultConfig);
      }
    } catch (error) {
      console.error('Error loading pricing config:', error);
    } finally {
      setPricingLoading(false);
    }
  };

  const savePricingConfig = async () => {
    if (!pricingConfig) return;
    setSavingPricing(true);
    try {
      const configRef = doc(db, 'siteConfig', 'pricing');
      await setDoc(configRef, pricingConfig);
      toast.success('Pricing saved');
    } catch (error) {
      console.error('Error saving pricing config:', error);
      toast.error('Failed to save pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const runMigration = async () => {
    if (migrationRunning) return;

    setMigrationRunning(true);
    setMigrationResult(null);
    setMigrationLogs(['Starting migration...']);

    try {
      const functions = getFunctions();
      const runMeetingMigration = httpsCallable(functions, 'runMeetingMigration');

      setMigrationLogs(prev => [...prev, 'Calling Cloud Function...']);

      const result = await runMeetingMigration();

      setMigrationResult(result.data);
      setMigrationLogs(prev => [
        ...prev,
        'Migration completed successfully!',
        `Scheduled meetings migrated: ${result.data.scheduledMeetings?.migrated || 0}`,
        `Existing meetings updated: ${result.data.existingMeetings?.updated || 0}`,
        `Total errors: ${result.data.totalErrors || 0}`
      ]);

      toast.success('Migration completed successfully');
    } catch (error) {
      console.error('Migration error:', error);
      setMigrationLogs(prev => [...prev, `Error: ${error.message}`]);
      setMigrationResult({ error: error.message });
      toast.error('Migration failed: ' + error.message);
    } finally {
      setMigrationRunning(false);
    }
  };

  const updatePricingField = (tier, field, value) => {
    setPricingConfig(prev => ({ ...prev, [tier]: { ...prev[tier], [field]: value } }));
  };

  const loadTermsConfig = async () => {
    setTermsLoading(true);
    try {
      const defaultConfig = { currentVersion: '1.0.0', requiredVersion: '1.0.0', privacyVersion: '1.0.0', effectiveDate: new Date().toISOString() };
      let configExists = false;
      let versionsExist = false;

      try {
        const configRef = doc(db, 'siteConfig', 'terms');
        const configSnap = await getDoc(configRef);
        if (configSnap.exists()) {
          setTermsConfig(configSnap.data());
          configExists = true;
        } else {
          setTermsConfig(defaultConfig);
        }
      } catch {
        setTermsConfig(defaultConfig);
      }

      try {
        const versionsRef = collection(db, 'termsVersions');
        const versionsQuery = query(versionsRef, orderBy('createdAt', 'desc'), limit(10));
        const versionsSnap = await getDocs(versionsQuery);
        const versions = versionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTermsVersions(versions);
        versionsExist = versions.length > 0;
      } catch {
        setTermsVersions([]);
      }

      setTermsDataSeeded(configExists && versionsExist);

      try {
        const acceptanceRef = collection(db, 'termsAcceptance');
        const acceptanceSnap = await getDocs(acceptanceRef);
        const total = acceptanceSnap.size;
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const recent = acceptanceSnap.docs.filter(doc => {
          const acceptedAt = doc.data().acceptedAt?.toDate?.();
          return acceptedAt && acceptedAt > thirtyDaysAgo;
        }).length;
        setAcceptanceStats({ total, recent });
      } catch {
        setAcceptanceStats({ total: 0, recent: 0 });
      }
    } catch (error) {
      console.error('Error loading terms config:', error);
    } finally {
      setTermsLoading(false);
    }
  };

  const loadTermsPreview = async () => {
    try {
      const { getTermsContent, getCurrentTermsConfig } = await import('../services/termsService');
      const config = await getCurrentTermsConfig();
      const termsContent = await getTermsContent(config.requiredVersion, 'terms');
      const privacyContent = await getTermsContent(config.privacyVersion, 'privacy');
      setTermsPreview({ config, terms: termsContent, privacy: privacyContent, usingFallback: !termsContent || !privacyContent });
      setShowTermsPreview(true);
    } catch (error) {
      console.error('Error loading terms preview:', error);
      toast.error('Failed to load preview');
    }
  };

  const saveTermsConfig = async () => {
    if (!termsConfig) return;
    setSavingTerms(true);
    try {
      const configRef = doc(db, 'siteConfig', 'terms');
      await setDoc(configRef, { ...termsConfig, updatedAt: serverTimestamp() });
      toast.success('Terms config saved');
    } catch (error) {
      console.error('Error saving terms config:', error);
      toast.error('Failed to save');
    } finally {
      setSavingTerms(false);
    }
  };

  const createNewVersion = async () => {
    if (!newVersion.version || !newVersion.content || !newVersion.title) {
      toast.error('Fill in all required fields');
      return;
    }
    setSavingTerms(true);
    try {
      const versionId = `${newVersion.type}_v${newVersion.version}`;
      const versionRef = doc(db, 'termsVersions', versionId);
      await setDoc(versionRef, {
        version: newVersion.version,
        type: newVersion.type,
        title: newVersion.title,
        content: newVersion.content,
        summary: newVersion.summary || '',
        createdAt: serverTimestamp(),
        effectiveDate: serverTimestamp(),
        previousVersion: termsConfig?.currentVersion || null
      });

      const configRef = doc(db, 'siteConfig', 'terms');
      const updateData = {};
      if (newVersion.type === 'terms') {
        updateData.currentVersion = newVersion.version;
        updateData.requiredVersion = newVersion.version;
      } else {
        updateData.privacyVersion = newVersion.version;
      }
      updateData.effectiveDate = new Date().toISOString();
      await setDoc(configRef, updateData, { merge: true });

      toast.success(`Version ${newVersion.version} created`);
      setShowNewVersionModal(false);
      setNewVersion({ version: '', type: 'terms', title: '', content: '', summary: '' });
      loadTermsConfig();
    } catch (error) {
      console.error('Error creating version:', error);
      toast.error('Failed to create version');
    } finally {
      setSavingTerms(false);
    }
  };

  const loadPlatformAnalytics = async () => {
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) throw new Error('Not authenticated');

      // Fetch all data in parallel
      const [platformRes, meetingRes, siteData] = await Promise.all([
        fetch('/api/admin?action=stats', { headers: { 'Authorization': `Bearer ${idToken}` } }),
        fetch('/api/admin?action=meeting-stats', { headers: { 'Authorization': `Bearer ${idToken}` } }),
        getAnalyticsSummary()
      ]);

      if (!platformRes.ok) throw new Error((await platformRes.json()).error || 'Failed to fetch stats');
      if (!meetingRes.ok) throw new Error((await meetingRes.json()).error || 'Failed to fetch meeting stats');

      setPlatformStats(await platformRes.json());
      setMeetingStats(await meetingRes.json());
      setSiteAnalytics(siteData);

      // Load cookie consent stats
      await loadConsentStats();
    } catch (error) {
      console.error('Error loading analytics:', error);
      setAnalyticsError(error.message);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const loadConsentStats = async () => {
    try {
      const consentRef = collection(db, 'consentLogs');
      const snapshot = await getDocs(consentRef);

      const logs = snapshot.docs.map(doc => doc.data());

      // Calculate stats
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const monthAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);

      const stats = {
        total: logs.length,
        thisWeek: logs.filter(l => {
          const date = l.timestamp?.toDate?.() || l.createdAt?.toDate?.();
          return date && date >= weekAgo;
        }).length,
        thisMonth: logs.filter(l => {
          const date = l.timestamp?.toDate?.() || l.createdAt?.toDate?.();
          return date && date >= monthAgo;
        }).length,
        acceptedAll: logs.filter(l => l.analytics && l.marketing && l.functionality).length,
        acceptedAnalytics: logs.filter(l => l.analytics).length,
        acceptedMarketing: logs.filter(l => l.marketing).length,
        acceptedFunctionality: logs.filter(l => l.functionality).length,
        essentialOnly: logs.filter(l => !l.analytics && !l.marketing && !l.functionality).length,
        uniqueUsers: new Set(logs.filter(l => l.userId).map(l => l.userId)).size,
        uniqueSessions: new Set(logs.map(l => l.sessionId)).size
      };

      setConsentStats(stats);
    } catch (error) {
      console.error('Error loading consent stats:', error);
    }
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'team': return 'bg-purple-500/10 text-purple-600 dark:text-purple-400';
      case 'pro': return 'bg-blue-500/10 text-blue-600 dark:text-blue-400';
      default: return 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
    }
  };

  const loadWaitlist = async () => {
    setWaitlistLoading(true);
    try {
      const waitlistRef = collection(db, 'waitlist');
      const snapshot = await getDocs(waitlistRef);

      const entries = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort by submittedAt descending
      entries.sort((a, b) => {
        const dateA = a.submittedAt?.toMillis?.() || 0;
        const dateB = b.submittedAt?.toMillis?.() || 0;
        return dateB - dateA;
      });

      setWaitlistEntries(entries);

      // Calculate stats
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

      const stats = {
        total: entries.length,
        pending: entries.filter(e => e.status === 'pending').length,
        approved: entries.filter(e => e.status === 'approved').length,
        rejected: entries.filter(e => e.status === 'rejected').length,
        thisWeek: entries.filter(e => {
          const submittedAt = e.submittedAt?.toDate?.();
          return submittedAt && submittedAt >= weekAgo;
        }).length
      };
      setWaitlistStats(stats);
    } catch (error) {
      console.error('Error loading waitlist:', error);
      toast.error('Failed to load waitlist');
    } finally {
      setWaitlistLoading(false);
    }
  };

  const updateWaitlistStatus = async (entryId, newStatus) => {
    try {
      const entry = waitlistEntries.find(e => e.id === entryId);
      const entryRef = doc(db, 'waitlist', entryId);
      await updateDoc(entryRef, {
        status: newStatus,
        updatedAt: serverTimestamp()
      });

      // Send approval email if status changed to 'approved' and user wants notification
      if (newStatus === 'approved' && entry?.email && entry?.notifyOnApproval !== false) {
        try {
          const response = await fetch('/api/send-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              to: entry.email,
              subject: 'Welcome to QuickMeet - You\'re Approved!',
              templateId: 'waitlist_approved',
              templateData: {
                firstName: entry.firstName || 'there',
                appStoreUrl: 'https://apps.apple.com/app/quickmeet'
              },
              type: 'transactional'
            })
          });

          if (response.ok) {
            toast.success('Approval email sent!');
          }
        } catch (emailError) {
          console.error('Failed to send approval email:', emailError);
          // Don't block the status update if email fails
        }
      }

      // Update local state
      setWaitlistEntries(prev => prev.map(e =>
        e.id === entryId ? { ...e, status: newStatus } : e
      ));

      // Update stats
      setWaitlistStats(prev => {
        const oldStatus = entry?.status || 'pending';
        return {
          ...prev,
          [oldStatus]: prev[oldStatus] - 1,
          [newStatus]: prev[newStatus] + 1
        };
      });

      toast.success(`Status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating waitlist status:', error);
      toast.error('Failed to update status');
    }
  };

  // Email management functions
  const loadEmailData = async () => {
    setEmailLoading(true);
    try {
      const token = await user.getIdToken();
      const [statsRes, configRes, logsRes, usageRes, templatesRes] = await Promise.all([
        fetch('/api/admin?action=email-stats', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch('/api/admin?action=email-config', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch('/api/admin?action=email-logs&limit=50', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch('/api/admin?action=email-usage', {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch('/api/admin?action=email-templates', {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);

      if (statsRes.ok) {
        const stats = await statsRes.json();
        setEmailStats(stats);
      }
      if (configRes.ok) {
        const config = await configRes.json();
        setEmailConfig(config);
      }
      if (logsRes.ok) {
        const { logs } = await logsRes.json();
        setEmailLogs(logs);
      }
      if (usageRes.ok) {
        const usage = await usageRes.json();
        setEmailUsage(usage);
      }
      if (templatesRes.ok) {
        const { templates } = await templatesRes.json();
        setEmailTemplates(templates);
      }
    } catch (error) {
      console.error('Error loading email data:', error);
      toast.error('Failed to load email data');
    } finally {
      setEmailLoading(false);
    }
  };

  const toggleEmergencyPause = async () => {
    try {
      const newPaused = !emailConfig?.globalToggles?.emergencyPause;
      const res = await fetch('/api/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({ action: 'toggle-pause', paused: newPaused })
      });

      if (res.ok) {
        setEmailConfig(prev => ({
          ...prev,
          globalToggles: { ...prev.globalToggles, emergencyPause: newPaused }
        }));
        toast.success(newPaused ? 'Email sending paused' : 'Email sending resumed');
      }
    } catch (error) {
      console.error('Error toggling pause:', error);
      toast.error('Failed to toggle email pause');
    }
  };

  const sendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Enter an email address');
      return;
    }
    setSendingEmail(true);
    try {
      const res = await fetch('/api/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({
          action: 'send-test',
          templateId: 'welcome',
          to: testEmailAddress
        })
      });

      if (res.ok) {
        toast.success('Test email sent');
        setShowTestEmailModal(false);
        setTestEmailAddress('');
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to send test email');
      }
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error('Failed to send test email');
    } finally {
      setSendingEmail(false);
    }
  };

  const sendCampaign = async () => {
    if (!campaignData.name || !campaignData.subject || !campaignData.html) {
      toast.error('Fill in all required fields');
      return;
    }
    setSendingEmail(true);
    try {
      const res = await fetch('/api/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({
          action: 'send-campaign',
          ...campaignData
        })
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(`Campaign sent to ${data.stats.sent} recipients`);
        setShowCampaignModal(false);
        setCampaignData({ name: '', subject: '', html: '', audience: 'all' });
        loadEmailData();
      } else {
        toast.error(data.error || 'Failed to send campaign');
      }
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast.error('Failed to send campaign');
    } finally {
      setSendingEmail(false);
    }
  };

  // Template management functions
  const saveTemplate = async (template) => {
    try {
      const res = await fetch('/api/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({ action: 'save-template', template })
      });

      if (res.ok) {
        toast.success('Template saved');
        setShowTemplateModal(false);
        setEditingTemplate(null);
        loadEmailData();
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to save template');
      }
    } catch (error) {
      console.error('Error saving template:', error);
      toast.error('Failed to save template');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!confirm('Delete this template?')) return;
    try {
      const res = await fetch('/api/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({ action: 'delete-template', templateId })
      });

      if (res.ok) {
        toast.success('Template deleted');
        loadEmailData();
      } else {
        const data = await res.json();
        toast.error(data.error || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    }
  };

  const sendTemplateEmail = async () => {
    const templateId = selectedTemplate?.id || sendEmailData.templateId;
    if (!templateId) {
      toast.error('Select a template');
      return;
    }
    if (sendEmailData.audience === 'individual' && !sendEmailData.recipients) {
      toast.error('Enter a recipient email');
      return;
    }

    setSendingEmail(true);
    try {
      const recipients = sendEmailData.audience === 'individual' && sendEmailData.recipients
        ? sendEmailData.recipients.split(',').map(e => e.trim()).filter(e => e)
        : null;

      const res = await fetch('/api/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({
          action: 'send-template-email',
          templateId,
          audience: recipients ? null : sendEmailData.audience,
          recipients,
          subject: sendEmailData.customSubject || null,
          variables: sendEmailData.variables
        })
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(`Email sent to ${data.stats?.sent || 1} recipient${data.stats?.sent !== 1 ? 's' : ''}`);
        setShowSendEmailModal(false);
        setSelectedTemplate(null);
        setSendEmailData({ templateId: '', audience: 'all', recipients: '', customSubject: '', variables: {} });
        loadEmailData();
      } else {
        toast.error(data.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  // Email Editor Functions
  const openEmailEditor = (template = null) => {
    if (template) {
      // If template has HTML content, use it directly instead of blocks
      if (template.html) {
        setEmailEditorData({
          subject: template.subject || '',
          previewMode: true, // Start in preview mode to show the template
          templateId: template.id,
          templateName: template.name,
          useTemplateHtml: true,
          templateHtml: template.html,
          blocks: [] // Empty blocks when using template HTML
        });
      } else {
        // No HTML, use block editor with template metadata
        setEmailEditorData({
          subject: template.subject || '',
          previewMode: false,
          templateId: template.id,
          templateName: template.name,
          useTemplateHtml: false,
          templateHtml: null,
          blocks: [
            { id: '1', type: 'heading', content: template.name || 'Email Title' },
            { id: '2', type: 'text', content: 'Hi {{firstName}},' },
            { id: '3', type: 'text', content: template.description || 'Write your email content here...' },
            { id: '4', type: 'button', content: 'Learn More', url: 'https://quickmeet.cam', color: '#0066cc' }
          ]
        });
      }
    } else {
      setEmailEditorData({
        subject: '',
        previewMode: false,
        templateId: null,
        templateName: null,
        useTemplateHtml: false,
        templateHtml: null,
        blocks: [
          { id: '1', type: 'heading', content: 'Your Email Title' },
          { id: '2', type: 'text', content: 'Hi {{firstName}},' },
          { id: '3', type: 'text', content: 'Write your email content here...' },
          { id: '4', type: 'button', content: 'Click Here', url: 'https://quickmeet.cam', color: '#0066cc' }
        ]
      });
    }
    setShowEmailEditor(true);
  };

  const addEmailBlock = (type) => {
    const newBlock = {
      id: Date.now().toString(),
      type,
      content: type === 'heading' ? 'New Heading' :
               type === 'button' ? 'Click Here' :
               type === 'image' ? '' :
               type === 'divider' ? '' : 'New text block...',
      ...(type === 'button' && { url: 'https://quickmeet.cam', color: '#0066cc' }),
      ...(type === 'image' && { url: '', alt: 'Image' })
    };
    setEmailEditorData(prev => ({
      ...prev,
      blocks: [...prev.blocks, newBlock]
    }));
  };

  const updateEmailBlock = (id, updates) => {
    setEmailEditorData(prev => ({
      ...prev,
      blocks: prev.blocks.map(block =>
        block.id === id ? { ...block, ...updates } : block
      )
    }));
  };

  const deleteEmailBlock = (id) => {
    setEmailEditorData(prev => ({
      ...prev,
      blocks: prev.blocks.filter(block => block.id !== id)
    }));
  };

  const moveEmailBlock = (id, direction) => {
    setEmailEditorData(prev => {
      const blocks = [...prev.blocks];
      const index = blocks.findIndex(b => b.id === id);
      if (direction === 'up' && index > 0) {
        [blocks[index], blocks[index - 1]] = [blocks[index - 1], blocks[index]];
      } else if (direction === 'down' && index < blocks.length - 1) {
        [blocks[index], blocks[index + 1]] = [blocks[index + 1], blocks[index]];
      }
      return { ...prev, blocks };
    });
  };

  const generateEmailHtml = (forPreview = true) => {
    // If using template HTML, return it directly
    if (emailEditorData.useTemplateHtml && emailEditorData.templateHtml) {
      // Only replace personalization tokens for preview, keep them for actual sending
      return forPreview
        ? emailEditorData.templateHtml.replace(/\{\{firstName\}\}/g, 'John')
        : emailEditorData.templateHtml;
    }

    // Otherwise generate from blocks
    // Using Gmail dark mode friendly styles with explicit colors
    return emailEditorData.blocks.map(block => {
      switch (block.type) {
        case 'heading':
          return `<h1 style="margin: 0 0 24px; color: #1d1d1f; background-color: #ffffff; font-size: 30px; font-weight: 700; letter-spacing: -0.5px; line-height: 1.2;">${block.content}</h1>`;
        case 'text':
          return `<p style="margin: 0 0 18px; color: #1d1d1f; background-color: #ffffff; font-size: 17px; line-height: 28px;">${block.content}</p>`;
        case 'button':
          return `<div style="text-align: center; margin: 36px 0; background-color: #ffffff;">
            <a href="${block.url || '#'}" style="display: inline-block; background-color: ${block.color || '#0066cc'}; color: #ffffff; -webkit-text-fill-color: #ffffff; padding: 18px 48px; border-radius: 14px; text-decoration: none; font-weight: 600; font-size: 17px; border: none;">${block.content}</a>
          </div>`;
        case 'image':
          return block.url ? `<div style="text-align: center; margin: 28px 0; background-color: #ffffff;">
            <img src="${block.url}" alt="${block.alt || ''}" style="max-width: 100%; border-radius: 16px; border: 1px solid #e5e5e7;" />
          </div>` : '';
        case 'divider':
          return `<hr style="border: none; border-top: 1px solid #e5e5e7; margin: 32px 0; background-color: #ffffff;" />`;
        default:
          return '';
      }
    }).join('\n');
  };

  // Helper to darken/lighten hex color for gradients
  const adjustColor = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min(255, Math.max(0, (num >> 16) + amt));
    const G = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amt));
    const B = Math.min(255, Math.max(0, (num & 0x0000FF) + amt));
    return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
  };

  const sendComposedEmail = async () => {
    if (!emailEditorData.subject) {
      toast.error('Please enter a subject line');
      return;
    }
    if (sendEmailData.audience === 'individual' && !sendEmailData.recipients) {
      toast.error('Enter a recipient email');
      return;
    }

    setSendingEmail(true);
    try {
      const html = generateEmailHtml(false); // false = don't replace personalization tokens
      const recipients = sendEmailData.audience === 'individual' && sendEmailData.recipients
        ? sendEmailData.recipients.split(',').map(e => e.trim()).filter(e => e)
        : null;

      const res = await fetch('/api/admin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await user.getIdToken()}`
        },
        body: JSON.stringify({
          action: 'send-composed-email',
          subject: emailEditorData.subject,
          html,
          audience: recipients ? null : sendEmailData.audience,
          recipients
        })
      });

      const data = await res.json();
      if (res.ok) {
        toast.success(`Email sent to ${data.stats?.sent || 1} recipient${data.stats?.sent !== 1 ? 's' : ''}`);
        setShowEmailEditor(false);
        setSendEmailData({ templateId: '', audience: 'all', recipients: '', customSubject: '', variables: {} });
        loadEmailData();
      } else {
        toast.error(data.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      toast.error('Failed to send email');
    } finally {
      setSendingEmail(false);
    }
  };

  const filteredWaitlist = waitlistEntries.filter(entry => {
    const matchesSearch =
      entry.email?.toLowerCase().includes(waitlistSearch.toLowerCase()) ||
      entry.firstName?.toLowerCase().includes(waitlistSearch.toLowerCase()) ||
      entry.lastName?.toLowerCase().includes(waitlistSearch.toLowerCase()) ||
      entry.companyName?.toLowerCase().includes(waitlistSearch.toLowerCase());

    const matchesFilter = waitlistFilter === 'all' || entry.status === waitlistFilter;

    return matchesSearch && matchesFilter;
  });

  // Enhanced filtering with tier, status, auth provider
  const filteredUsers = users
    .filter(u => {
      // Search filter
      const matchesSearch =
        u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.lastName?.toLowerCase().includes(searchTerm.toLowerCase());

      // Tier filter
      const matchesTier = tierFilter === 'all' ||
        (tierFilter === 'free' && (!u.subscriptionTier || u.subscriptionTier === 'free')) ||
        u.subscriptionTier === tierFilter;

      // Status filter
      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && u.subscriptionStatus === 'active' && !u.disabled) ||
        (statusFilter === 'inactive' && u.subscriptionStatus !== 'active' && !u.disabled) ||
        (statusFilter === 'disabled' && u.disabled);

      // Auth provider filter
      const matchesAuthProvider = authProviderFilter === 'all' ||
        (authProviderFilter === 'google' && u.authProvider === 'google') ||
        (authProviderFilter === 'email' && (!u.authProvider || u.authProvider === 'email' || u.authProvider === 'password')) ||
        (authProviderFilter === 'apple' && u.authProvider === 'apple');

      return matchesSearch && matchesTier && matchesStatus && matchesAuthProvider;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0);
        case 'oldest':
          return (a.createdAt?.toMillis?.() || 0) - (b.createdAt?.toMillis?.() || 0);
        case 'name_asc':
          return (a.displayName || a.email || '').localeCompare(b.displayName || b.email || '');
        case 'name_desc':
          return (b.displayName || b.email || '').localeCompare(a.displayName || a.email || '');
        case 'last_login':
          return (b.lastLoginAt?.toMillis?.() || 0) - (a.lastLoginAt?.toMillis?.() || 0);
        case 'tier':
          const tierOrder = { team: 3, pro: 2, free: 1 };
          return (tierOrder[b.subscriptionTier] || 1) - (tierOrder[a.subscriptionTier] || 1);
        default:
          return 0;
      }
    });

  // Bulk selection helpers
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  };

  const selectAllUsers = () => {
    if (selectedUsers.size === filteredUsers.length) {
      setSelectedUsers(new Set());
    } else {
      setSelectedUsers(new Set(filteredUsers.map(u => u.id)));
    }
  };

  const clearSelection = () => {
    setSelectedUsers(new Set());
  };

  // Bulk tier change
  const bulkUpdateTier = async (newTier) => {
    if (selectedUsers.size === 0) return;
    setBulkActionLoading(true);
    try {
      const promises = Array.from(selectedUsers).map(userId => {
        const userRef = doc(db, 'users', userId);
        const subscriptionStatus = newTier === 'pro' || newTier === 'team' ? 'active' : 'inactive';
        const isPremium = newTier !== 'free';
        return updateDoc(userRef, {
          subscriptionTier: newTier,
          subscriptionStatus,
          isPremium,
          testMode: true,
          testModeSetAt: new Date().toISOString(),
          testModeSetBy: user.email
        });
      });
      await Promise.all(promises);

      setUsers(prevUsers =>
        prevUsers.map(u =>
          selectedUsers.has(u.id)
            ? { ...u, subscriptionTier: newTier, subscriptionStatus: newTier === 'pro' || newTier === 'team' ? 'active' : 'inactive', isPremium: newTier !== 'free', testMode: true }
            : u
        )
      );

      toast.success(`Updated ${selectedUsers.size} users to ${newTier.toUpperCase()}`);
      clearSelection();
    } catch (error) {
      console.error('Bulk update error:', error);
      toast.error('Failed to update some users');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Toggle admin role
  const toggleAdminRole = async (userId, currentIsAdmin) => {
    setUpdatingUserId(userId);
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, { isAdmin: !currentIsAdmin });

      setUsers(prevUsers =>
        prevUsers.map(u =>
          u.id === userId ? { ...u, isAdmin: !currentIsAdmin } : u
        )
      );

      toast.success(currentIsAdmin ? 'Admin role revoked' : 'Admin role granted');
    } catch (error) {
      console.error('Error toggling admin:', error);
      toast.error('Failed to update admin role');
    } finally {
      setUpdatingUserId(null);
    }
  };

  // Export users to CSV
  const exportToCSV = () => {
    const dataToExport = selectedUsers.size > 0
      ? users.filter(u => selectedUsers.has(u.id))
      : filteredUsers;

    const headers = ['Email', 'Display Name', 'First Name', 'Last Name', 'Tier', 'Status', 'Auth Provider', 'Created At', 'Last Login', 'Is Admin', 'Disabled'];
    const rows = dataToExport.map(u => [
      u.email || '',
      u.displayName || '',
      u.firstName || '',
      u.lastName || '',
      u.subscriptionTier || 'free',
      u.subscriptionStatus || 'inactive',
      u.authProvider || 'email',
      u.createdAt?.toDate?.()?.toISOString() || '',
      u.lastLoginAt?.toDate?.()?.toISOString() || '',
      u.isAdmin ? 'Yes' : 'No',
      u.disabled ? 'Yes' : 'No'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `quickmeet-users-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success(`Exported ${dataToExport.length} users to CSV`);
  };

  // View user profile
  const viewUserProfile = (userId) => {
    const targetUser = users.find(u => u.id === userId);
    setProfileModalUser(targetUser);
    setShowProfileModal(true);
  };

  if (!isAdmin) return null;

  const tabs = [
    { id: 'analytics', label: 'Analytics', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>
    )},
    { id: 'users', label: 'Users', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>
    )},
    { id: 'waitlist', label: 'Waitlist', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
      </svg>
    )},
    { id: 'emails', label: 'Emails', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    )},
    { id: 'settings', label: 'Settings', icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    )}
  ];

  return (
    <div className="min-h-screen bg-[#f8f8fa] dark:bg-[#0a0a0a]">
      {/* Header */}
      <header className="bg-white/80 dark:bg-[#1a1a1a]/80 backdrop-blur-xl border-b border-black/5 dark:border-white/5 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-xl transition-colors"
              >
                <svg className="w-5 h-5 text-[#1d1d1f] dark:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
              </button>
              <div>
                <h1 className="text-lg font-semibold text-[#1d1d1f] dark:text-white">Admin Console</h1>
                <p className="text-xs text-[#86868b]">Platform management</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="px-3 py-1.5 bg-red-500/10 text-red-600 dark:text-red-400 text-xs font-medium rounded-full">
                Admin
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 pb-3 overflow-x-auto">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-[#1d1d1f] dark:bg-white text-white dark:text-black'
                    : 'text-[#86868b] hover:text-[#1d1d1f] dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-[#1d1d1f] dark:text-white">Platform Analytics</h2>
                <p className="text-sm text-[#86868b]">Real-time performance metrics</p>
              </div>
              <button
                onClick={loadPlatformAnalytics}
                disabled={analyticsLoading}
                className="px-4 py-2 bg-[#1d1d1f] dark:bg-white text-white dark:text-black text-sm font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {analyticsLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {analyticsLoading && !platformStats ? (
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-12 text-center border border-black/5 dark:border-white/5">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1d1d1f] dark:border-white border-t-transparent mx-auto"></div>
                <p className="text-[#86868b] mt-3 text-sm">Loading analytics...</p>
              </div>
            ) : analyticsError ? (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-2xl p-6 border border-red-200 dark:border-red-800">
                <p className="text-red-600 dark:text-red-400 font-medium">Failed to load analytics</p>
                <p className="text-red-500 text-sm mt-1">{analyticsError}</p>
                <button onClick={loadPlatformAnalytics} className="mt-3 text-sm text-red-600 underline">Retry</button>
              </div>
            ) : platformStats && meetingStats ? (
              <>
                {/* KPI Cards - Row 1 */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-5 border border-black/5 dark:border-white/5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </div>
                      <span className="text-xs font-medium text-green-500">+{platformStats.users?.today || 0} new</span>
                    </div>
                    <div className="text-3xl font-bold text-[#1d1d1f] dark:text-white">{platformStats.users?.total || 0}</div>
                    <div className="text-xs text-[#86868b] mt-1">Total Users</div>
                  </div>

                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-5 border border-black/5 dark:border-white/5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-10 h-10 bg-green-500/10 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      </div>
                      <span className="text-xs font-medium text-[#86868b]">today</span>
                    </div>
                    <div className="text-3xl font-bold text-[#1d1d1f] dark:text-white">{siteAnalytics?.today?.visitors || 0}</div>
                    <div className="text-xs text-[#86868b] mt-1">Visitors Today</div>
                  </div>

                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-5 border border-black/5 dark:border-white/5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <span className="text-xs font-medium text-green-500">+{meetingStats.overview?.meetingsToday || 0} today</span>
                    </div>
                    <div className="text-3xl font-bold text-[#1d1d1f] dark:text-white">{meetingStats.overview?.totalMeetings || 0}</div>
                    <div className="text-xs text-[#86868b] mt-1">Total Meetings</div>
                  </div>

                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-5 border border-black/5 dark:border-white/5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-10 h-10 bg-purple-500/10 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                      <span className="text-xs font-medium text-[#86868b]">unique</span>
                    </div>
                    <div className="text-3xl font-bold text-[#1d1d1f] dark:text-white">{meetingStats.overview?.usersWithMeetings || 0}</div>
                    <div className="text-xs text-[#86868b] mt-1">Users Made Meetings</div>
                  </div>
                </div>

                {/* KPI Cards - Row 2: Revenue & Subscriptions */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-5 border border-black/5 dark:border-white/5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-10 h-10 bg-emerald-500/10 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="text-xs font-medium text-[#86868b]">{platformStats.revenue?.currency || 'AUD'}</span>
                    </div>
                    <div className="text-3xl font-bold text-[#1d1d1f] dark:text-white">${platformStats.revenue?.mrr || '0'}</div>
                    <div className="text-xs text-[#86868b] mt-1">Monthly Revenue</div>
                  </div>

                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-5 border border-black/5 dark:border-white/5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-10 h-10 bg-cyan-500/10 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-cyan-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="text-xs font-medium text-[#86868b]">{platformStats.subscriptions?.trialing || 0} trial</span>
                    </div>
                    <div className="text-3xl font-bold text-[#1d1d1f] dark:text-white">{platformStats.subscriptions?.active || 0}</div>
                    <div className="text-xs text-[#86868b] mt-1">Active Subscribers</div>
                  </div>

                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-5 border border-black/5 dark:border-white/5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-10 h-10 bg-amber-500/10 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="text-xs font-medium text-[#86868b]">this week</span>
                    </div>
                    <div className="text-3xl font-bold text-[#1d1d1f] dark:text-white">{siteAnalytics?.week?.views || 0}</div>
                    <div className="text-xs text-[#86868b] mt-1">Page Views (Week)</div>
                  </div>

                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-5 border border-black/5 dark:border-white/5">
                    <div className="flex items-center justify-between mb-4">
                      <div className="w-10 h-10 bg-rose-500/10 rounded-xl flex items-center justify-center">
                        <svg className="w-5 h-5 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                        </svg>
                      </div>
                      <span className="text-xs font-medium text-[#86868b]">active</span>
                    </div>
                    <div className="text-3xl font-bold text-[#1d1d1f] dark:text-white">{meetingStats.overview?.activeCreatorsThisWeek || 0}</div>
                    <div className="text-xs text-[#86868b] mt-1">Active Creators (Week)</div>
                  </div>
                </div>

                {/* Metrics Row */}
                <div className="grid grid-cols-5 gap-3">
                  {[
                    { label: 'Conversion', value: `${platformStats.metrics?.conversionRate || 0}%`, color: 'blue' },
                    { label: 'Churn', value: `${platformStats.metrics?.churnRate || 0}%`, color: 'red' },
                    { label: '7-Day Retention', value: `${platformStats.metrics?.retentionRate7Day || 0}%`, color: 'green' },
                    { label: '30-Day Retention', value: `${platformStats.metrics?.retentionRate30Day || 0}%`, color: 'purple' },
                    { label: 'ARPU', value: `$${platformStats.revenue?.arpu || 0}`, color: 'orange' }
                  ].map((metric, i) => (
                    <div key={i} className="bg-white dark:bg-[#1a1a1a] rounded-xl p-4 border border-black/5 dark:border-white/5 text-center">
                      <div className="text-xl font-bold text-[#1d1d1f] dark:text-white">{metric.value}</div>
                      <div className="text-[10px] text-[#86868b] uppercase tracking-wide mt-1">{metric.label}</div>
                    </div>
                  ))}
                </div>

                {/* Cookie Consent Analytics */}
                {consentStats && (
                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-[#1d1d1f] dark:text-white">Cookie Consent Analytics</h3>
                      <span className="text-xs text-[#86868b]">{consentStats.total} total responses</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                      <div className="text-center p-3 bg-green-500/10 rounded-xl">
                        <div className="text-2xl font-bold text-green-600 dark:text-green-400">{consentStats.acceptedAll}</div>
                        <div className="text-[10px] text-[#86868b] uppercase mt-1">Accepted All</div>
                      </div>
                      <div className="text-center p-3 bg-blue-500/10 rounded-xl">
                        <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{consentStats.acceptedAnalytics}</div>
                        <div className="text-[10px] text-[#86868b] uppercase mt-1">Analytics</div>
                      </div>
                      <div className="text-center p-3 bg-purple-500/10 rounded-xl">
                        <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{consentStats.acceptedMarketing}</div>
                        <div className="text-[10px] text-[#86868b] uppercase mt-1">Marketing</div>
                      </div>
                      <div className="text-center p-3 bg-gray-500/10 rounded-xl">
                        <div className="text-2xl font-bold text-gray-600 dark:text-gray-400">{consentStats.essentialOnly}</div>
                        <div className="text-[10px] text-[#86868b] uppercase mt-1">Essential Only</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-4">
                        <span className="text-[#86868b]">This week: <span className="text-[#1d1d1f] dark:text-white font-medium">{consentStats.thisWeek}</span></span>
                        <span className="text-[#86868b]">This month: <span className="text-[#1d1d1f] dark:text-white font-medium">{consentStats.thisMonth}</span></span>
                      </div>
                      <div className="flex items-center gap-4">
                        <span className="text-[#86868b]">Unique users: <span className="text-[#1d1d1f] dark:text-white font-medium">{consentStats.uniqueUsers}</span></span>
                        <span className="text-[#86868b]">Sessions: <span className="text-[#1d1d1f] dark:text-white font-medium">{consentStats.uniqueSessions}</span></span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Charts */}
                <div className="grid lg:grid-cols-2 gap-6">
                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
                    <h3 className="font-semibold text-[#1d1d1f] dark:text-white mb-4">User Growth</h3>
                    <div className="h-40 flex items-end gap-2">
                      {platformStats.growth?.weekly?.map((week, i) => {
                        const max = Math.max(...platformStats.growth.weekly.map(w => w.newUsers), 1);
                        const height = (week.newUsers / max) * 100;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <div
                              className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-lg transition-all hover:from-blue-600 hover:to-blue-500"
                              style={{ height: `${Math.max(height, 8)}%` }}
                            ></div>
                            <span className="text-[10px] text-[#86868b] font-medium">{week.newUsers}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-xs text-[#86868b] text-center mt-3">Last 8 weeks</div>
                  </div>

                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
                    <h3 className="font-semibold text-[#1d1d1f] dark:text-white mb-4">Meeting Volume</h3>
                    <div className="h-40 flex items-end gap-2">
                      {meetingStats.trends?.weekly?.map((week, i) => {
                        const max = Math.max(...meetingStats.trends.weekly.map(w => w.count), 1);
                        const height = (week.count / max) * 100;
                        return (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <div
                              className="w-full bg-gradient-to-t from-orange-500 to-orange-400 rounded-lg transition-all hover:from-orange-600 hover:to-orange-500"
                              style={{ height: `${Math.max(height, 8)}%` }}
                            ></div>
                            <span className="text-[10px] text-[#86868b] font-medium">{week.count}</span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="text-xs text-[#86868b] text-center mt-3">Last 8 weeks</div>
                  </div>
                </div>

                {/* Insights */}
                <div className="grid lg:grid-cols-3 gap-6">
                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
                    <h3 className="font-semibold text-[#1d1d1f] dark:text-white mb-4">Peak Hours</h3>
                    <div className="space-y-3">
                      {meetingStats.peakUsage?.peakHours?.map((peak, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              i === 0 ? 'bg-yellow-500/20 text-yellow-600' : 'bg-gray-500/10 text-gray-500'
                            }`}>{i + 1}</span>
                            <span className="text-[#1d1d1f] dark:text-white font-medium">{peak.hourFormatted}</span>
                          </div>
                          <span className="text-sm text-[#86868b]">{peak.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
                    <h3 className="font-semibold text-[#1d1d1f] dark:text-white mb-4">Peak Days</h3>
                    <div className="space-y-3">
                      {meetingStats.peakUsage?.peakDays?.map((peak, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              i === 0 ? 'bg-yellow-500/20 text-yellow-600' : 'bg-gray-500/10 text-gray-500'
                            }`}>{i + 1}</span>
                            <span className="text-[#1d1d1f] dark:text-white font-medium">{peak.dayName}</span>
                          </div>
                          <span className="text-sm text-[#86868b]">{peak.count}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
                    <h3 className="font-semibold text-[#1d1d1f] dark:text-white mb-4">Top Creators</h3>
                    <div className="space-y-3">
                      {meetingStats.creators?.topCreators?.slice(0, 3).map((creator, i) => (
                        <div key={i} className="flex items-center justify-between">
                          <span className="text-sm text-[#1d1d1f] dark:text-white truncate max-w-[150px]">{creator.email}</span>
                          <span className="text-sm text-[#86868b] font-medium">{creator.count}</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-4 pt-4 border-t border-black/5 dark:border-white/5">
                      <span className="text-xs text-[#86868b]">{meetingStats.creators?.uniqueCreators || 0} unique creators</span>
                    </div>
                  </div>
                </div>

                {/* Activity Summary */}
                <div className="bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-orange-500/5 rounded-2xl p-6 border border-black/5 dark:border-white/5">
                  <div className="grid grid-cols-4 gap-6 text-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{platformStats.users?.activeUsersLast7Days || 0}</div>
                      <div className="text-xs text-[#86868b] mt-1">Users Active (7 days)</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{platformStats.users?.activeUsersLast30Days || 0}</div>
                      <div className="text-xs text-[#86868b] mt-1">Users Active (30 days)</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">{meetingStats.overview?.meetingsThisWeek || 0}</div>
                      <div className="text-xs text-[#86868b] mt-1">Meetings This Week</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">{siteAnalytics?.month?.visitors || 0}</div>
                      <div className="text-xs text-[#86868b] mt-1">Visitors (30 days)</div>
                    </div>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Header with Stats */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-[#1d1d1f] dark:text-white">User Management</h2>
                <p className="text-sm text-[#86868b]">{users.length} total users</p>
              </div>
              <div className="flex items-center gap-3">
                {/* Tier Summary Pills */}
                <div className="flex items-center gap-2 bg-white dark:bg-[#1a1a1a] rounded-xl px-3 py-2 border border-black/5 dark:border-white/5">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-gray-400"></span>
                    <span className="text-xs text-[#86868b]">{users.filter(u => !u.subscriptionTier || u.subscriptionTier === 'free').length} Free</span>
                  </span>
                  <span className="w-px h-4 bg-black/10 dark:bg-white/10"></span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    <span className="text-xs text-[#86868b]">{users.filter(u => u.subscriptionTier === 'pro').length} Pro</span>
                  </span>
                  <span className="w-px h-4 bg-black/10 dark:bg-white/10"></span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                    <span className="text-xs text-[#86868b]">{users.filter(u => u.subscriptionTier === 'team').length} Team</span>
                  </span>
                </div>
                <button
                  onClick={loadUsers}
                  disabled={isLoading}
                  className="p-2.5 bg-[#1d1d1f] dark:bg-white text-white dark:text-black rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  <svg className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Search & Filter Bar */}
            <div className="space-y-3">
              <div className="flex flex-col lg:flex-row gap-3">
                {/* Search */}
                <div className="relative flex-1">
                  <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name or email..."
                    className="w-full pl-11 pr-4 py-3 bg-white dark:bg-[#1a1a1a] border border-black/5 dark:border-white/5 rounded-xl text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg"
                    >
                      <svg className="w-4 h-4 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Filter Dropdowns */}
                <div className="flex flex-wrap gap-2">
                  {/* Tier Filter */}
                  <select
                    value={tierFilter}
                    onChange={(e) => setTierFilter(e.target.value)}
                    className="px-3 py-2.5 bg-white dark:bg-[#1a1a1a] border border-black/5 dark:border-white/5 rounded-xl text-sm text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  >
                    <option value="all">All Tiers</option>
                    <option value="free">Free</option>
                    <option value="pro">Pro</option>
                    <option value="team">Team</option>
                  </select>

                  {/* Status Filter */}
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2.5 bg-white dark:bg-[#1a1a1a] border border-black/5 dark:border-white/5 rounded-xl text-sm text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  >
                    <option value="all">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="disabled">Disabled</option>
                  </select>

                  {/* Auth Provider Filter */}
                  <select
                    value={authProviderFilter}
                    onChange={(e) => setAuthProviderFilter(e.target.value)}
                    className="px-3 py-2.5 bg-white dark:bg-[#1a1a1a] border border-black/5 dark:border-white/5 rounded-xl text-sm text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  >
                    <option value="all">All Providers</option>
                    <option value="email">Email</option>
                    <option value="google">Google</option>
                    <option value="apple">Apple</option>
                  </select>

                  {/* Sort By */}
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-3 py-2.5 bg-white dark:bg-[#1a1a1a] border border-black/5 dark:border-white/5 rounded-xl text-sm text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="name_asc">Name A-Z</option>
                    <option value="name_desc">Name Z-A</option>
                    <option value="last_login">Last Login</option>
                    <option value="tier">By Tier</option>
                  </select>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={exportToCSV}
                    className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-[#1a1a1a] border border-black/5 dark:border-white/5 rounded-xl text-sm text-[#1d1d1f] dark:text-white hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                    title="Export to CSV"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <span className="hidden sm:inline">Export</span>
                  </button>
                </div>
              </div>

              {/* Results Count & Bulk Selection */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedUsers.size === filteredUsers.length && filteredUsers.length > 0}
                      onChange={selectAllUsers}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-[#86868b]">Select All</span>
                  </label>
                  <span className="text-sm text-[#86868b]">
                    {selectedUsers.size > 0 ? `${selectedUsers.size} selected` : `Showing ${filteredUsers.length} of ${users.length}`}
                  </span>
                </div>

                {/* Clear Filters */}
                {(tierFilter !== 'all' || statusFilter !== 'all' || authProviderFilter !== 'all' || searchTerm) && (
                  <button
                    onClick={() => {
                      setTierFilter('all');
                      setStatusFilter('all');
                      setAuthProviderFilter('all');
                      setSearchTerm('');
                    }}
                    className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400"
                  >
                    Clear Filters
                  </button>
                )}
              </div>

              {/* Bulk Action Bar */}
              {selectedUsers.size > 0 && (
                <div className="flex items-center gap-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl">
                  <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                    {selectedUsers.size} user{selectedUsers.size !== 1 ? 's' : ''} selected
                  </span>
                  <div className="flex-1" />
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => bulkUpdateTier('free')}
                      disabled={bulkActionLoading}
                      className="px-3 py-1.5 text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 disabled:opacity-50"
                    >
                      Set Free
                    </button>
                    <button
                      onClick={() => bulkUpdateTier('pro')}
                      disabled={bulkActionLoading}
                      className="px-3 py-1.5 text-xs font-medium bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
                    >
                      Set Pro
                    </button>
                    <button
                      onClick={() => bulkUpdateTier('team')}
                      disabled={bulkActionLoading}
                      className="px-3 py-1.5 text-xs font-medium bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50"
                    >
                      Set Team
                    </button>
                    <span className="w-px h-6 bg-blue-500/20" />
                    <button
                      onClick={clearSelection}
                      className="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-500/10 rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Users Grid */}
            {isLoading ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {[1, 2, 3, 4, 5, 6].map(i => (
                  <div key={i} className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-5 border border-black/5 dark:border-white/5 animate-pulse">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-black/5 dark:bg-white/5 rounded-full"></div>
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-black/5 dark:bg-white/5 rounded w-3/4"></div>
                        <div className="h-3 bg-black/5 dark:bg-white/5 rounded w-1/2"></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-12 text-center border border-black/5 dark:border-white/5">
                <svg className="w-12 h-12 text-[#86868b] mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <p className="text-[#1d1d1f] dark:text-white font-medium">No users found</p>
                <p className="text-sm text-[#86868b] mt-1">Try adjusting your search</p>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {filteredUsers.map(u => (
                  <div key={u.id} className={`bg-white dark:bg-[#1a1a1a] rounded-2xl border overflow-hidden transition-colors ${
                    selectedUsers.has(u.id) ? 'ring-2 ring-blue-500 border-blue-500' :
                    u.disabled || u.deleted
                      ? 'border-red-200 dark:border-red-900 opacity-60'
                      : 'border-black/5 dark:border-white/5 hover:border-black/10 dark:hover:border-white/10'
                  }`}>
                    {/* Selection & Admin Badges Header */}
                    <div className="px-5 pt-3 pb-0 flex items-center justify-between">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={selectedUsers.has(u.id)}
                          onChange={() => toggleUserSelection(u.id)}
                          className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </label>
                      <div className="flex items-center gap-2">
                        {u.isAdmin && (
                          <span className="px-2 py-0.5 bg-red-500/10 text-red-600 dark:text-red-400 text-[10px] font-semibold rounded-full">
                            ADMIN
                          </span>
                        )}
                        {u.testMode && !u.disabled && (
                          <span className="px-1.5 py-0.5 bg-yellow-500/10 text-yellow-600 text-[10px] font-medium rounded">
                            TEST
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Disabled Banner */}
                    {(u.disabled || u.deleted) && (
                      <div className="mx-5 mt-2 px-3 py-2 bg-red-500/10 border border-red-200 dark:border-red-900 rounded-lg flex items-center justify-between">
                        <span className="text-xs font-medium text-red-600 dark:text-red-400">
                          {u.deleted ? 'Account Deleted' : 'Account Disabled'}
                        </span>
                        <button
                          onClick={() => deleteUser(u.id, 'enable')}
                          disabled={deletingUserId === u.id}
                          className="text-xs text-red-600 dark:text-red-400 underline hover:no-underline disabled:opacity-50"
                        >
                          {deletingUserId === u.id ? 'Restoring...' : 'Restore'}
                        </button>
                      </div>
                    )}

                    {/* User Header - Clickable */}
                    <div className="p-5 cursor-pointer" onClick={() => viewUserProfile(u.id)}>
                      <div className="flex items-start gap-4">
                        {/* Avatar */}
                        <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-semibold text-lg ${
                          u.disabled || u.deleted ? 'bg-gradient-to-br from-red-400 to-red-500' :
                          u.subscriptionTier === 'team' ? 'bg-gradient-to-br from-purple-500 to-purple-600' :
                          u.subscriptionTier === 'pro' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                          'bg-gradient-to-br from-gray-400 to-gray-500'
                        }`}>
                          {(u.displayName || u.email || '?').charAt(0).toUpperCase()}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className={`font-medium truncate ${u.disabled || u.deleted ? 'text-red-600 dark:text-red-400 line-through' : 'text-[#1d1d1f] dark:text-white'}`}>
                              {u.displayName || 'No name'}
                            </h3>
                          </div>
                          <p className="text-sm text-[#86868b] truncate">{u.email}</p>
                        </div>

                        {/* Tier Badge */}
                        <span className={`flex-shrink-0 px-2.5 py-1 rounded-lg text-xs font-semibold ${
                          u.disabled || u.deleted ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400' :
                          u.subscriptionTier === 'team' ? 'bg-purple-500 text-white' :
                          u.subscriptionTier === 'pro' ? 'bg-blue-500 text-white' :
                          'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                        }`}>
                          {u.disabled || u.deleted ? 'DISABLED' : (u.subscriptionTier || 'free').toUpperCase()}
                        </span>
                      </div>

                      {/* User Details */}
                      <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                        <div className="flex items-center gap-2 text-[#86868b]">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>
                            {u.createdAt?.toDate ? u.createdAt.toDate().toLocaleDateString() : 'Unknown'}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 text-[#86868b]">
                          {u.disabled || u.deleted ? (
                            <>
                              <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>
                              <span className="text-red-600">Disabled</span>
                            </>
                          ) : u.subscriptionStatus === 'active' ? (
                            <>
                              <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>
                              <span className="text-green-600">Active</span>
                            </>
                          ) : (
                            <>
                              <span className="w-1.5 h-1.5 rounded-full bg-gray-400"></span>
                              <span>Inactive</span>
                            </>
                          )}
                        </div>
                        {/* Auth Provider */}
                        <div className="flex items-center gap-2 text-[#86868b]">
                          {u.authProvider === 'google' ? (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                            </svg>
                          ) : u.authProvider === 'apple' ? (
                            <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                            </svg>
                          ) : (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          )}
                          <span>{u.authProvider === 'google' ? 'Google' : u.authProvider === 'apple' ? 'Apple' : 'Email'}</span>
                        </div>
                        {/* Last Login */}
                        <div className="flex items-center gap-2 text-[#86868b]">
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>
                            {u.lastLoginAt?.toDate ? u.lastLoginAt.toDate().toLocaleDateString() : 'Never'}
                          </span>
                        </div>
                        {u.subscriptionTier === 'team' && u.teamSeats && !u.disabled && (
                          <div className="col-span-2 flex items-center gap-2 text-purple-600">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span>{u.teamSeatsUsed || 1} / {u.teamSeats} seats used</span>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="px-5 py-3 bg-black/[0.02] dark:bg-white/[0.02] border-t border-black/5 dark:border-white/5 space-y-2">
                      {u.disabled || u.deleted ? (
                        <button
                          onClick={() => deleteUser(u.id, 'enable')}
                          disabled={deletingUserId === u.id}
                          className="w-full py-2 text-xs font-medium rounded-lg bg-green-500/10 text-green-600 hover:bg-green-500/20 transition-all disabled:opacity-50"
                        >
                          {deletingUserId === u.id ? 'Restoring...' : 'Restore Account'}
                        </button>
                      ) : (
                        <>
                          {/* Tier Buttons */}
                          <div className="flex gap-2">
                            <button
                              onClick={() => updateUserTier(u.id, 'free')}
                              disabled={updatingUserId === u.id || u.subscriptionTier === 'free' || !u.subscriptionTier}
                              className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all disabled:opacity-40 ${
                                u.subscriptionTier === 'free' || !u.subscriptionTier
                                  ? 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                                  : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                              }`}
                            >
                              Free
                            </button>
                            <button
                              onClick={() => updateUserTier(u.id, 'pro')}
                              disabled={updatingUserId === u.id || u.subscriptionTier === 'pro'}
                              className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all disabled:opacity-40 ${
                                u.subscriptionTier === 'pro'
                                  ? 'bg-blue-500 text-white'
                                  : 'bg-blue-500/10 text-blue-600 hover:bg-blue-500/20'
                              }`}
                            >
                              Pro
                            </button>
                            <button
                              onClick={() => handleTeamTierClick(u.id)}
                              disabled={updatingUserId === u.id || u.subscriptionTier === 'team'}
                              className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all disabled:opacity-40 ${
                                u.subscriptionTier === 'team'
                                  ? 'bg-purple-500 text-white'
                                  : 'bg-purple-500/10 text-purple-600 hover:bg-purple-500/20'
                              }`}
                            >
                              Team
                            </button>
                          </div>
                          {/* Admin & Delete Row */}
                          <div className="flex gap-2">
                            <button
                              onClick={(e) => { e.stopPropagation(); toggleAdminRole(u.id, u.isAdmin); }}
                              disabled={updatingUserId === u.id || u.id === user?.uid}
                              className={`flex-1 py-2 text-xs font-medium rounded-lg transition-all disabled:opacity-40 ${
                                u.isAdmin
                                  ? 'bg-red-500 text-white hover:bg-red-600'
                                  : 'bg-red-500/10 text-red-600 hover:bg-red-500/20'
                              }`}
                              title={u.id === user?.uid ? "Can't modify your own admin status" : u.isAdmin ? 'Revoke Admin' : 'Grant Admin'}
                            >
                              {u.isAdmin ? 'Revoke Admin' : 'Make Admin'}
                            </button>
                            <button
                              onClick={() => viewUserProfile(u.id)}
                              className="flex-1 py-2 text-xs font-medium rounded-lg bg-black/5 dark:bg-white/5 text-[#1d1d1f] dark:text-white hover:bg-black/10 dark:hover:bg-white/10 transition-all"
                            >
                              View Profile
                            </button>
                            <button
                              onClick={() => handleDeleteClick(u.id)}
                              disabled={updatingUserId === u.id}
                              className="p-2 text-red-500 hover:bg-red-500/10 rounded-lg transition-all disabled:opacity-40"
                              title="Delete user"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Info Card */}
            <div className="bg-gradient-to-r from-blue-500/5 via-purple-500/5 to-pink-500/5 rounded-2xl p-5 border border-black/5 dark:border-white/5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-500/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-medium text-[#1d1d1f] dark:text-white">Test Mode Subscriptions</h4>
                  <p className="text-sm text-[#86868b] mt-1">
                    Changing user tiers here is for testing purposes only. Users marked as "TEST" will have access to features without Stripe billing.
                    For production subscriptions, users should upgrade through the normal checkout flow.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Waitlist Tab */}
        {activeTab === 'waitlist' && (
          <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-[#1d1d1f] dark:text-white">iOS Waitlist</h2>
                <p className="text-sm text-[#86868b]">Manage iOS app waitlist signups</p>
              </div>
              <button
                onClick={loadWaitlist}
                disabled={waitlistLoading}
                className="px-4 py-2 bg-[#1d1d1f] dark:bg-white text-white dark:text-black text-sm font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                {waitlistLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {/* Analytics Cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-4 border border-black/5 dark:border-white/5">
                <div className="text-2xl font-bold text-[#1d1d1f] dark:text-white">{waitlistStats.total}</div>
                <div className="text-sm text-[#86868b]">Total Signups</div>
              </div>
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-4 border border-black/5 dark:border-white/5">
                <div className="text-2xl font-bold text-amber-500">{waitlistStats.pending}</div>
                <div className="text-sm text-[#86868b]">Pending</div>
              </div>
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-4 border border-black/5 dark:border-white/5">
                <div className="text-2xl font-bold text-green-500">{waitlistStats.approved}</div>
                <div className="text-sm text-[#86868b]">Approved</div>
              </div>
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-4 border border-black/5 dark:border-white/5">
                <div className="text-2xl font-bold text-red-500">{waitlistStats.rejected}</div>
                <div className="text-sm text-[#86868b]">Rejected</div>
              </div>
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-4 border border-black/5 dark:border-white/5">
                <div className="text-2xl font-bold text-blue-500">{waitlistStats.thisWeek}</div>
                <div className="text-sm text-[#86868b]">This Week</div>
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  placeholder="Search by name, email, or company..."
                  value={waitlistSearch}
                  onChange={(e) => setWaitlistSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-[#1a1a1a] border border-black/5 dark:border-white/5 rounded-xl text-[#1d1d1f] dark:text-white placeholder-[#86868b] focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                />
              </div>
              <div className="flex gap-2">
                {['all', 'pending', 'approved', 'rejected'].map(filter => (
                  <button
                    key={filter}
                    onClick={() => setWaitlistFilter(filter)}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${
                      waitlistFilter === filter
                        ? 'bg-[#1d1d1f] dark:bg-white text-white dark:text-black'
                        : 'bg-white dark:bg-[#1a1a1a] text-[#86868b] hover:text-[#1d1d1f] dark:hover:text-white border border-black/5 dark:border-white/5'
                    }`}
                  >
                    {filter.charAt(0).toUpperCase() + filter.slice(1)}
                  </button>
                ))}
              </div>
            </div>

            {/* Waitlist Table */}
            <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl border border-black/5 dark:border-white/5 overflow-hidden">
              {waitlistLoading ? (
                <div className="p-12 text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#1d1d1f] dark:border-white border-t-transparent mx-auto"></div>
                </div>
              ) : filteredWaitlist.length === 0 ? (
                <div className="p-12 text-center">
                  <svg className="w-12 h-12 text-[#86868b] mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                  <p className="text-[#86868b]">No waitlist entries found</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-black/5 dark:border-white/5">
                        <th className="text-left px-4 py-3 text-xs font-medium text-[#86868b] uppercase tracking-wider">Name</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-[#86868b] uppercase tracking-wider">Email</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-[#86868b] uppercase tracking-wider">Type</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-[#86868b] uppercase tracking-wider">Use Case</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-[#86868b] uppercase tracking-wider">Industry</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-[#86868b] uppercase tracking-wider">Company</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-[#86868b] uppercase tracking-wider">Date</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-[#86868b] uppercase tracking-wider">Status</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-[#86868b] uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-black/5 dark:divide-white/5">
                      {filteredWaitlist.map(entry => (
                        <tr key={entry.id} className="hover:bg-black/[0.02] dark:hover:bg-white/[0.02]">
                          <td className="px-4 py-3">
                            <div className="font-medium text-[#1d1d1f] dark:text-white">
                              {entry.firstName} {entry.lastName}
                            </div>
                            {entry.role && (
                              <div className="text-xs text-[#86868b]">{entry.role}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-[#1d1d1f] dark:text-white">{entry.email}</td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              entry.usageType === 'professional'
                                ? 'bg-purple-500/10 text-purple-600 dark:text-purple-400'
                                : 'bg-cyan-500/10 text-cyan-600 dark:text-cyan-400'
                            }`}>
                              {entry.usageType || 'N/A'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="px-2 py-1 bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-medium rounded-full">
                              {entry.useCase?.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-[#86868b]">{entry.industry?.replace(/_/g, ' ')}</td>
                          <td className="px-4 py-3 text-sm text-[#86868b]">{entry.companyName || '-'}</td>
                          <td className="px-4 py-3 text-sm text-[#86868b]">
                            {entry.submittedAt?.toDate?.().toLocaleDateString() || '-'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                              entry.status === 'approved' ? 'bg-green-500/10 text-green-600 dark:text-green-400' :
                              entry.status === 'rejected' ? 'bg-red-500/10 text-red-600 dark:text-red-400' :
                              'bg-amber-500/10 text-amber-600 dark:text-amber-400'
                            }`}>
                              {entry.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex items-center justify-end gap-1">
                              {entry.status !== 'approved' && (
                                <button
                                  onClick={() => updateWaitlistStatus(entry.id, 'approved')}
                                  className="p-1.5 hover:bg-green-500/10 rounded-lg transition-colors"
                                  title="Approve"
                                >
                                  <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                  </svg>
                                </button>
                              )}
                              {entry.status !== 'rejected' && (
                                <button
                                  onClick={() => updateWaitlistStatus(entry.id, 'rejected')}
                                  className="p-1.5 hover:bg-red-500/10 rounded-lg transition-colors"
                                  title="Reject"
                                >
                                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              )}
                              {entry.status !== 'pending' && (
                                <button
                                  onClick={() => updateWaitlistStatus(entry.id, 'pending')}
                                  className="p-1.5 hover:bg-amber-500/10 rounded-lg transition-colors"
                                  title="Set Pending"
                                >
                                  <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Industry & Use Case Breakdown */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
                <h3 className="font-semibold text-[#1d1d1f] dark:text-white mb-4">By Industry</h3>
                <div className="space-y-2">
                  {Object.entries(
                    waitlistEntries.reduce((acc, e) => {
                      const industry = e.industry || 'other';
                      acc[industry] = (acc[industry] || 0) + 1;
                      return acc;
                    }, {})
                  ).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([industry, count]) => (
                    <div key={industry} className="flex items-center justify-between">
                      <span className="text-sm text-[#86868b] capitalize">{industry.replace(/_/g, ' ')}</span>
                      <span className="text-sm font-medium text-[#1d1d1f] dark:text-white">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
                <h3 className="font-semibold text-[#1d1d1f] dark:text-white mb-4">By Use Case</h3>
                <div className="space-y-2">
                  {Object.entries(
                    waitlistEntries.reduce((acc, e) => {
                      const useCase = e.useCase || 'other';
                      acc[useCase] = (acc[useCase] || 0) + 1;
                      return acc;
                    }, {})
                  ).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([useCase, count]) => (
                    <div key={useCase} className="flex items-center justify-between">
                      <span className="text-sm text-[#86868b] capitalize">{useCase.replace(/_/g, ' ')}</span>
                      <span className="text-sm font-medium text-[#1d1d1f] dark:text-white">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Emails Tab */}
        {activeTab === 'emails' && (
          <div className="space-y-6">
            {/* Header with Actions */}
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-[#1d1d1f] dark:text-white">Email Management</h2>
                <p className="text-sm text-[#86868b]">Send emails, manage templates, and monitor usage</p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowSendEmailModal(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors text-sm"
                >
                  Send Email
                </button>
                <button
                  onClick={loadEmailData}
                  disabled={emailLoading}
                  className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg transition-colors"
                >
                  <svg className={`w-5 h-5 ${emailLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Sub-tabs */}
            <div className="flex gap-2 border-b border-black/5 dark:border-white/5">
              {['overview', 'templates', 'logs'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setEmailSubTab(tab)}
                  className={`px-4 py-2 text-sm font-medium capitalize transition-colors ${
                    emailSubTab === tab
                      ? 'text-blue-600 border-b-2 border-blue-600'
                      : 'text-[#86868b] hover:text-[#1d1d1f] dark:hover:text-white'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </div>

            {/* OVERVIEW TAB */}
            {emailSubTab === 'overview' && (
              <>
                {/* Emergency Pause Banner */}
                {emailConfig?.globalToggles?.emergencyPause && (
                  <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-2xl p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-red-800 dark:text-red-200">Email Sending Paused</p>
                        <p className="text-sm text-red-600 dark:text-red-400">All non-transactional emails are currently paused</p>
                      </div>
                    </div>
                    <button
                      onClick={toggleEmergencyPause}
                      className="px-4 py-2 bg-red-600 text-white rounded-xl font-medium hover:bg-red-700 transition-colors text-sm"
                    >
                      Resume Sending
                    </button>
                  </div>
                )}

                {/* Email Quota / Usage */}
                {emailUsage && (
                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
                    <h3 className="font-semibold text-[#1d1d1f] dark:text-white mb-4">Email Quota (Resend)</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Daily Usage */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-[#86868b]">Daily Usage</span>
                          <span className="text-sm font-medium text-[#1d1d1f] dark:text-white">
                            {emailUsage.usage?.today || 0} / {emailUsage.limits?.daily || 100}
                          </span>
                        </div>
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              parseFloat(emailUsage.percentUsed?.daily || 0) > 80 ? 'bg-red-500' :
                              parseFloat(emailUsage.percentUsed?.daily || 0) > 50 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(100, emailUsage.percentUsed?.daily || 0)}%` }}
                          />
                        </div>
                        <p className="text-xs text-[#86868b] mt-1">{emailUsage.remaining?.daily || 0} remaining today</p>
                      </div>
                      {/* Monthly Usage */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-[#86868b]">Monthly Usage</span>
                          <span className="text-sm font-medium text-[#1d1d1f] dark:text-white">
                            {emailUsage.usage?.thisMonth || 0} / {emailUsage.limits?.monthly || 3000}
                          </span>
                        </div>
                        <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              parseFloat(emailUsage.percentUsed?.monthly || 0) > 80 ? 'bg-red-500' :
                              parseFloat(emailUsage.percentUsed?.monthly || 0) > 50 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(100, emailUsage.percentUsed?.monthly || 0)}%` }}
                          />
                        </div>
                        <p className="text-xs text-[#86868b] mt-1">{emailUsage.remaining?.monthly || 0} remaining this month</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-5 border border-black/5 dark:border-white/5">
                <p className="text-sm text-[#86868b] mb-1">Total Sent</p>
                <p className="text-2xl font-semibold text-[#1d1d1f] dark:text-white">
                  {emailStats?.total?.toLocaleString() || 0}
                </p>
              </div>
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-5 border border-black/5 dark:border-white/5">
                <p className="text-sm text-[#86868b] mb-1">Last 24 Hours</p>
                <p className="text-2xl font-semibold text-[#1d1d1f] dark:text-white">
                  {emailStats?.last24h?.toLocaleString() || 0}
                </p>
              </div>
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-5 border border-black/5 dark:border-white/5">
                <p className="text-sm text-[#86868b] mb-1">Last 7 Days</p>
                <p className="text-2xl font-semibold text-[#1d1d1f] dark:text-white">
                  {emailStats?.last7d?.toLocaleString() || 0}
                </p>
              </div>
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-5 border border-black/5 dark:border-white/5">
                <p className="text-sm text-[#86868b] mb-1">Last 30 Days</p>
                <p className="text-2xl font-semibold text-[#1d1d1f] dark:text-white">
                  {emailStats?.last30d?.toLocaleString() || 0}
                </p>
              </div>
            </div>

            {/* Stats by Type & Status */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
                <h3 className="font-semibold text-[#1d1d1f] dark:text-white mb-4">By Type</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#86868b]">Transactional</span>
                    <span className="text-sm font-medium text-[#1d1d1f] dark:text-white">{emailStats?.byType?.transactional || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#86868b]">Marketing</span>
                    <span className="text-sm font-medium text-[#1d1d1f] dark:text-white">{emailStats?.byType?.marketing || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#86868b]">System</span>
                    <span className="text-sm font-medium text-[#1d1d1f] dark:text-white">{emailStats?.byType?.system || 0}</span>
                  </div>
                </div>
              </div>
              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
                <h3 className="font-semibold text-[#1d1d1f] dark:text-white mb-4">By Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#86868b]">Sent</span>
                    <span className="text-sm font-medium text-green-600">{emailStats?.byStatus?.sent || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#86868b]">Delivered</span>
                    <span className="text-sm font-medium text-blue-600">{emailStats?.byStatus?.delivered || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#86868b]">Failed</span>
                    <span className="text-sm font-medium text-red-600">{emailStats?.byStatus?.failed || 0}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Global Controls */}
            <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
              <h3 className="font-semibold text-[#1d1d1f] dark:text-white mb-4">Global Controls</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-black/5 dark:border-white/5">
                  <div>
                    <p className="font-medium text-[#1d1d1f] dark:text-white">Emergency Pause</p>
                    <p className="text-sm text-[#86868b]">Stop all non-transactional emails immediately</p>
                  </div>
                  <button
                    onClick={toggleEmergencyPause}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      emailConfig?.globalToggles?.emergencyPause ? 'bg-red-600' : 'bg-gray-200 dark:bg-gray-700'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        emailConfig?.globalToggles?.emergencyPause ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <div className="flex items-center justify-between py-3">
                  <div>
                    <p className="font-medium text-[#1d1d1f] dark:text-white">Email Provider</p>
                    <p className="text-sm text-[#86868b]">Resend API</p>
                  </div>
                  <span className="px-3 py-1 bg-green-500/10 text-green-600 rounded-full text-sm font-medium">Connected</span>
                </div>
              </div>
            </div>
              </>
            )}

            {/* TEMPLATES TAB */}
            {emailSubTab === 'templates' && (
              <div className="space-y-6">
                {/* Actions Bar */}
                <div className="flex items-center justify-between">
                  <p className="text-sm text-[#86868b]">{emailTemplates.length} templates available</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setEditingTemplate({
                        id: '',
                        name: '',
                        subject: '',
                        description: '',
                        category: 'marketing',
                        html: `<div style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; max-width: 600px; margin: 0 auto; padding: 40px;">
  <h1 style="color: #1d1d1f; font-size: 28px;">Your Title Here</h1>
  <p style="color: #1d1d1f; font-size: 16px; line-height: 26px;">Hi {{firstName}},</p>
  <p style="color: #1d1d1f; font-size: 16px; line-height: 26px;">Your email content goes here...</p>
  <div style="text-align: center; margin: 32px 0;">
    <a href="https://quickmeet.cam" style="background-color: #0066cc; color: white; padding: 16px 32px; border-radius: 12px; text-decoration: none; font-weight: 600;">Click Here</a>
  </div>
</div>`,
                        isNew: true
                      })}
                      className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-xl font-medium hover:bg-green-700 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      New Template
                    </button>
                    <button
                      onClick={() => openEmailEditor(null)}
                      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                      Compose Email
                    </button>
                  </div>
                </div>

                {/* Templates Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {emailTemplates.map((template) => (
                    <div
                      key={template.id}
                      className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-5 border border-black/5 dark:border-white/5 hover:border-blue-500/30 transition-colors"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-medium text-[#1d1d1f] dark:text-white">{template.name}</h4>
                          <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                            template.category === 'transactional' ? 'bg-blue-500/10 text-blue-600' :
                            template.category === 'marketing' ? 'bg-purple-500/10 text-purple-600' :
                            template.category === 'onboarding' ? 'bg-green-500/10 text-green-600' :
                            template.category === 'feedback' ? 'bg-orange-500/10 text-orange-600' :
                            'bg-gray-500/10 text-gray-600'
                          }`}>
                            {template.category}
                          </span>
                        </div>
                        {template.isDefault && (
                          <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full text-xs">
                            Default
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-[#86868b] mb-3 line-clamp-2">{template.description}</p>
                      <p className="text-xs text-[#86868b] mb-4">Subject: {template.subject}</p>
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEmailEditor(template)}
                          className="flex-1 px-3 py-2 bg-blue-500/10 text-blue-600 rounded-xl text-sm font-medium hover:bg-blue-500/20 transition-colors"
                        >
                          Use
                        </button>
                        <button
                          onClick={() => setEditingTemplate({ ...template, isNew: false })}
                          className="px-3 py-2 bg-gray-500/10 text-gray-600 dark:text-gray-400 rounded-xl text-sm font-medium hover:bg-gray-500/20 transition-colors"
                          title={template.isDefault ? 'Edit (creates custom copy)' : 'Edit template'}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        {!template.isDefault && (
                          <button
                            onClick={() => deleteTemplate(template.id)}
                            className="px-3 py-2 text-red-600 hover:bg-red-500/10 rounded-xl transition-colors"
                            title="Delete template"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {emailTemplates.length === 0 && (
                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-12 border border-black/5 dark:border-white/5 text-center">
                    <svg className="w-12 h-12 text-[#86868b] mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    <p className="text-[#86868b] mb-4">No templates available</p>
                    <button
                      onClick={loadEmailData}
                      className="px-4 py-2 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                    >
                      Reload Templates
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* LOGS TAB */}
            {emailSubTab === 'logs' && (
              <>
                {/* Recent Email Logs */}
            <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl border border-black/5 dark:border-white/5">
              <div className="px-6 py-4 border-b border-black/5 dark:border-white/5 flex items-center justify-between">
                <h3 className="font-semibold text-[#1d1d1f] dark:text-white">Recent Emails</h3>
                <span className="text-sm text-[#86868b]">{emailLogs.length} entries</span>
              </div>
              {emailLoading ? (
                <div className="p-12 text-center">
                  <div className="w-8 h-8 border-2 border-[#1d1d1f] dark:border-white border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-[#86868b]">Loading email logs...</p>
                </div>
              ) : emailLogs.length === 0 ? (
                <div className="p-12 text-center">
                  <svg className="w-12 h-12 text-[#86868b] mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <p className="text-[#86868b]">No emails sent yet</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-xs text-[#86868b] uppercase tracking-wider">
                        <th className="px-6 py-3 font-medium">Recipient</th>
                        <th className="px-6 py-3 font-medium">Subject</th>
                        <th className="px-6 py-3 font-medium">Type</th>
                        <th className="px-6 py-3 font-medium">Status</th>
                        <th className="px-6 py-3 font-medium">Sent</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-black/5 dark:divide-white/5">
                      {emailLogs.slice(0, 20).map((log) => (
                        <tr key={log.id} className="hover:bg-black/[0.02] dark:hover:bg-white/[0.02]">
                          <td className="px-6 py-3">
                            <span className="text-sm text-[#1d1d1f] dark:text-white">{log.email}</span>
                          </td>
                          <td className="px-6 py-3">
                            <span className="text-sm text-[#86868b] truncate max-w-[200px] block">{log.subject}</span>
                          </td>
                          <td className="px-6 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.type === 'transactional' ? 'bg-blue-500/10 text-blue-600' :
                              log.type === 'marketing' ? 'bg-purple-500/10 text-purple-600' :
                              'bg-gray-500/10 text-gray-600'
                            }`}>
                              {log.type || 'unknown'}
                            </span>
                          </td>
                          <td className="px-6 py-3">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              log.status === 'sent' ? 'bg-green-500/10 text-green-600' :
                              log.status === 'delivered' ? 'bg-blue-500/10 text-blue-600' :
                              log.status === 'failed' ? 'bg-red-500/10 text-red-600' :
                              'bg-gray-500/10 text-gray-600'
                            }`}>
                              {log.status}
                            </span>
                          </td>
                          <td className="px-6 py-3">
                            <span className="text-sm text-[#86868b]">
                              {log.sentAt ? new Date(log.sentAt).toLocaleString() : '-'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
              </>
            )}
          </div>
        )}

        {/* Send Email Modal */}
        {showSendEmailModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-black/5 dark:border-white/5 flex items-center justify-between sticky top-0 bg-white dark:bg-[#1a1a1a]">
                <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white">
                  {selectedTemplate ? `Send: ${selectedTemplate.name}` : 'Send Email'}
                </h3>
                <button
                  onClick={() => {
                    setShowSendEmailModal(false);
                    setSelectedTemplate(null);
                    setSendEmailData({ templateId: '', audience: 'all', recipients: '', customSubject: '', variables: {} });
                  }}
                  className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6 space-y-4">
                {/* Template Selection */}
                {!selectedTemplate && (
                  <div>
                    <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                      Select Template *
                    </label>
                    <select
                      value={sendEmailData.templateId}
                      onChange={(e) => {
                        const template = emailTemplates.find(t => t.id === e.target.value);
                        setSendEmailData(prev => ({ ...prev, templateId: e.target.value }));
                        if (template) setSelectedTemplate(template);
                      }}
                      className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Choose a template...</option>
                      {emailTemplates.map(t => (
                        <option key={t.id} value={t.id}>{t.name} ({t.category})</option>
                      ))}
                    </select>
                  </div>
                )}

                {selectedTemplate && (
                  <>
                    {/* Template Preview */}
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4">
                      <p className="text-sm text-[#86868b] mb-1">Subject:</p>
                      <p className="text-[#1d1d1f] dark:text-white font-medium">{selectedTemplate.subject}</p>
                    </div>

                    {/* Audience Selection */}
                    <div>
                      <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                        Send To *
                      </label>
                      <select
                        value={sendEmailData.audience}
                        onChange={(e) => setSendEmailData(prev => ({ ...prev, audience: e.target.value }))}
                        className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <optgroup label="Users">
                          <option value="individual">Individual Email</option>
                          <option value="all">All Users</option>
                          <option value="free">Free Tier Users</option>
                          <option value="pro">Pro Subscribers</option>
                          <option value="team">Team Subscribers</option>
                          <option value="beta">Beta Testers</option>
                          <option value="new_users">New Users (Last 7 days)</option>
                        </optgroup>
                        <optgroup label="Waitlist - By Usage">
                          <option value="waitlist_all">All Waitlist</option>
                          <option value="waitlist_personal">Personal Use</option>
                          <option value="waitlist_professional">Professional Use</option>
                        </optgroup>
                        <optgroup label="Waitlist - By Industry">
                          <option value="waitlist_industry_tech">Tech / Software</option>
                          <option value="waitlist_industry_agency">Agency / Creative</option>
                          <option value="waitlist_industry_education">Education</option>
                          <option value="waitlist_industry_healthcare">Healthcare</option>
                          <option value="waitlist_industry_finance">Finance</option>
                          <option value="waitlist_industry_startup">Startup / Founder</option>
                          <option value="waitlist_industry_freelance">Freelance</option>
                          <option value="waitlist_industry_enterprise">Enterprise</option>
                        </optgroup>
                        <optgroup label="Waitlist - By Team Size">
                          <option value="waitlist_size_solo">Solo (Just me)</option>
                          <option value="waitlist_size_small">Small (2-5 people)</option>
                          <option value="waitlist_size_medium">Medium (6-50 people)</option>
                          <option value="waitlist_size_large">Large (51+ people)</option>
                        </optgroup>
                      </select>
                    </div>

                    {/* Individual Email Input */}
                    {sendEmailData.audience === 'individual' && (
                      <div>
                        <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                          Recipient Email *
                        </label>
                        <input
                          type="email"
                          value={sendEmailData.recipients}
                          onChange={(e) => setSendEmailData(prev => ({ ...prev, recipients: e.target.value }))}
                          placeholder="user@example.com"
                          className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}

                    {/* Audience Info */}
                    {sendEmailData.audience !== 'individual' && (
                      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-3">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          {sendEmailData.audience.startsWith('waitlist_') ? (
                            <>This will send to waitlist entries matching: <strong>{sendEmailData.audience.replace('waitlist_', '').replace('industry_', '').replace('size_', '').replace(/_/g, ' ')}</strong></>
                          ) : (
                            <>This will send to all {sendEmailData.audience === 'all' ? 'users' : sendEmailData.audience.replace('_', ' ')} who have opted in to marketing emails.</>
                          )}
                        </p>
                      </div>
                    )}
                  </>
                )}

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <button
                    onClick={() => {
                      setShowSendEmailModal(false);
                      setSelectedTemplate(null);
                      setSendEmailData({ templateId: '', audience: 'all', recipients: '', customSubject: '', variables: {} });
                    }}
                    className="flex-1 px-4 py-3 border border-black/10 dark:border-white/10 rounded-xl font-medium hover:bg-black/5 dark:hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendTemplateEmail}
                    disabled={sendingEmail || !selectedTemplate || (sendEmailData.audience === 'individual' && !sendEmailData.recipients)}
                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {sendingEmail ? 'Sending...' : 'Send Email'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Editor Modal */}
        {showEmailEditor && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl w-full max-w-5xl max-h-[95vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-black/5 dark:border-white/5 flex items-center justify-between shrink-0">
                <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white">Email Composer</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setEmailEditorData(prev => ({ ...prev, previewMode: !prev.previewMode }))}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      emailEditorData.previewMode
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                    }`}
                  >
                    {emailEditorData.previewMode ? 'Edit' : 'Preview'}
                  </button>
                  <button
                    onClick={() => setShowEmailEditor(false)}
                    className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-hidden flex">
                {/* Left Panel - Editor */}
                <div className={`${emailEditorData.previewMode ? 'hidden md:block md:w-1/2' : 'w-full md:w-1/2'} border-r border-black/5 dark:border-white/5 overflow-y-auto p-6`}>
                  {/* Template Mode Banner */}
                  {emailEditorData.useTemplateHtml && (
                    <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-lg bg-blue-500 flex items-center justify-center shrink-0">
                          <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-blue-900 dark:text-blue-100">Using Template: {emailEditorData.templateName}</h4>
                          <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                            This email uses the pre-built template HTML. Preview it on the right, or switch to block editor to customize.
                          </p>
                          <button
                            onClick={() => setEmailEditorData(prev => ({
                              ...prev,
                              useTemplateHtml: false,
                              blocks: [
                                { id: '1', type: 'heading', content: prev.templateName || 'Email Title' },
                                { id: '2', type: 'text', content: 'Hi {{firstName}},' },
                                { id: '3', type: 'text', content: 'Write your email content here...' },
                                { id: '4', type: 'button', content: 'Learn More', url: 'https://quickmeet.cam', color: '#0066cc' }
                              ]
                            }))}
                            className="mt-3 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                          >
                            Switch to Block Editor →
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Subject Line */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">Subject Line</label>
                    <input
                      type="text"
                      value={emailEditorData.subject}
                      onChange={(e) => setEmailEditorData(prev => ({ ...prev, subject: e.target.value }))}
                      placeholder="Enter email subject..."
                      className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  {/* Add Block Buttons - Only show when not using template HTML */}
                  {!emailEditorData.useTemplateHtml && (
                  <>
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">Add Element</label>
                    <div className="flex flex-wrap gap-2">
                      <button onClick={() => addEmailBlock('heading')} className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" /></svg>
                        Heading
                      </button>
                      <button onClick={() => addEmailBlock('text')} className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>
                        Text
                      </button>
                      <button onClick={() => addEmailBlock('button')} className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" /></svg>
                        Button
                      </button>
                      <button onClick={() => addEmailBlock('image')} className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                        Image
                      </button>
                      <button onClick={() => addEmailBlock('divider')} className="px-3 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" /></svg>
                        Divider
                      </button>
                    </div>
                  </div>

                  {/* Content Blocks */}
                  <div className="space-y-4">
                    {emailEditorData.blocks.map((block, index) => (
                      <div key={block.id} className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs font-medium text-gray-500 uppercase">{block.type}</span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => moveEmailBlock(block.id, 'up')} disabled={index === 0} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded disabled:opacity-30">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" /></svg>
                            </button>
                            <button onClick={() => moveEmailBlock(block.id, 'down')} disabled={index === emailEditorData.blocks.length - 1} className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded disabled:opacity-30">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                            </button>
                            <button onClick={() => deleteEmailBlock(block.id)} className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-600">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            </button>
                          </div>
                        </div>

                        {block.type === 'heading' && (
                          <input
                            type="text"
                            value={block.content}
                            onChange={(e) => updateEmailBlock(block.id, { content: e.target.value })}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-lg font-semibold"
                            placeholder="Heading text..."
                          />
                        )}

                        {block.type === 'text' && (
                          <textarea
                            value={block.content}
                            onChange={(e) => updateEmailBlock(block.id, { content: e.target.value })}
                            rows={3}
                            className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 resize-none"
                            placeholder="Text content..."
                          />
                        )}

                        {block.type === 'button' && (
                          <div className="space-y-2">
                            <input
                              type="text"
                              value={block.content}
                              onChange={(e) => updateEmailBlock(block.id, { content: e.target.value })}
                              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900"
                              placeholder="Button text..."
                            />
                            <input
                              type="url"
                              value={block.url}
                              onChange={(e) => updateEmailBlock(block.id, { url: e.target.value })}
                              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm"
                              placeholder="https://..."
                            />
                            <div className="flex items-center gap-2">
                              <label className="text-sm text-gray-500">Color:</label>
                              <input
                                type="color"
                                value={block.color || '#0066cc'}
                                onChange={(e) => updateEmailBlock(block.id, { color: e.target.value })}
                                className="w-8 h-8 rounded cursor-pointer"
                              />
                            </div>
                          </div>
                        )}

                        {block.type === 'image' && (
                          <div className="space-y-2">
                            <input
                              type="url"
                              value={block.url || ''}
                              onChange={(e) => updateEmailBlock(block.id, { url: e.target.value })}
                              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900"
                              placeholder="Image URL (https://...)..."
                            />
                            <input
                              type="text"
                              value={block.alt || ''}
                              onChange={(e) => updateEmailBlock(block.id, { alt: e.target.value })}
                              className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm"
                              placeholder="Alt text..."
                            />
                          </div>
                        )}

                        {block.type === 'divider' && (
                          <div className="border-t border-gray-300 dark:border-gray-600 my-2"></div>
                        )}
                      </div>
                    ))}
                  </div>
                  </>
                  )}
                </div>

                {/* Right Panel - Preview */}
                <div className={`${emailEditorData.previewMode ? 'w-full' : 'hidden md:block md:w-1/2'} bg-[#f5f5f7] dark:bg-gray-900 overflow-y-auto p-6`}>
                  {/* Subject Preview */}
                  <div className="max-w-[600px] mx-auto mb-4">
                    <div className="bg-white dark:bg-gray-800 rounded-lg px-4 py-3 shadow-sm">
                      <p className="text-xs text-gray-500 mb-1">Subject:</p>
                      <p className="font-medium text-gray-900 dark:text-white">{emailEditorData.subject || '(No subject)'}</p>
                    </div>
                  </div>

                  {/* Template HTML Mode - Render template directly */}
                  {emailEditorData.useTemplateHtml ? (
                    <div className="bg-white rounded-[20px] shadow-lg max-w-[600px] mx-auto overflow-hidden" style={{ boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)' }}>
                      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(generateEmailHtml()) }} />
                    </div>
                  ) : (
                    <>
                    {/* Block Editor Mode - Email Preview with Professional Template */}
                    <div className="bg-white rounded-[20px] shadow-lg max-w-[600px] mx-auto overflow-hidden" style={{ boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)' }}>
                      {/* Logo Header */}
                      <div className="px-12 py-8 text-center border-b border-gray-100" style={{ background: 'linear-gradient(180deg, #ffffff 0%, #fafafa 100%)' }}>
                        <div className="inline-flex items-center gap-3">
                          <div className="w-11 h-11 rounded-xl flex items-center justify-center text-white text-xl font-bold" style={{ background: 'linear-gradient(135deg, #0066cc 0%, #0052a3 100%)' }}>
                            Q
                          </div>
                          <span className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.5px' }}>QuickMeet</span>
                        </div>
                      </div>

                      {/* Email Content */}
                      <div className="px-12 py-10" dangerouslySetInnerHTML={{ __html: generateEmailHtml() }} />

                      {/* Footer */}
                      <div className="px-12 py-8 border-t border-gray-100" style={{ background: 'linear-gradient(180deg, #fafafa 0%, #f5f5f7 100%)' }}>
                        <p className="text-gray-900 text-[15px] font-medium mb-1">Cheers,</p>
                        <p className="text-gray-900 text-[17px] font-semibold mb-1">– Jhye</p>
                        <p className="text-gray-500 text-[13px] font-medium mb-6">Founder & CEO, QuickMeet</p>

                        <hr className="border-gray-200 my-6" />

                        <div className="text-center">
                          <p className="text-gray-500 text-xs mb-3">
                            <span className="text-blue-600 font-medium">Website</span>
                            <span className="text-gray-300 mx-3">·</span>
                            <span className="text-blue-600 font-medium">Twitter</span>
                            <span className="text-gray-300 mx-3">·</span>
                            <span className="text-blue-600 font-medium">Support</span>
                          </p>
                          <p className="text-gray-400 text-[11px] mb-2">Professional Video Conferencing Made Simple</p>
                          <p className="text-gray-400 text-[11px]">
                            <span className="underline">Unsubscribe</span>
                            <span className="mx-2">|</span>
                            <span className="underline">Privacy Policy</span>
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Copyright */}
                    <p className="text-center text-gray-400 text-[11px] mt-6">
                      © {new Date().getFullYear()} QuickMeet. All rights reserved.
                    </p>
                    </>
                  )}
                </div>
              </div>

              {/* Footer - Send Options */}
              <div className="px-6 py-4 border-t border-black/5 dark:border-white/5 shrink-0 bg-gray-50 dark:bg-gray-900/50">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                  <div className="flex-1 flex flex-col md:flex-row items-start md:items-center gap-4">
                    <div className="w-full md:w-auto">
                      <select
                        value={sendEmailData.audience}
                        onChange={(e) => setSendEmailData(prev => ({ ...prev, audience: e.target.value }))}
                        className="w-full md:w-48 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
                      >
                        <option value="individual">Individual</option>
                        <option value="all">All Users</option>
                        <option value="free">Free Tier</option>
                        <option value="pro">Pro Subscribers</option>
                        <option value="team">Team Subscribers</option>
                        <option value="beta">Beta Testers</option>
                        <option value="new_users">New Users (7 days)</option>
                      </select>
                    </div>
                    {sendEmailData.audience === 'individual' && (
                      <input
                        type="email"
                        value={sendEmailData.recipients}
                        onChange={(e) => setSendEmailData(prev => ({ ...prev, recipients: e.target.value }))}
                        placeholder="recipient@example.com"
                        className="w-full md:w-64 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm"
                      />
                    )}
                  </div>
                  <div className="flex gap-2 w-full md:w-auto">
                    <button
                      onClick={() => setShowEmailEditor(false)}
                      className="flex-1 md:flex-none px-4 py-2 border border-gray-200 dark:border-gray-700 rounded-lg font-medium hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={sendComposedEmail}
                      disabled={sendingEmail || !emailEditorData.subject || (sendEmailData.audience === 'individual' && !sendEmailData.recipients)}
                      className="flex-1 md:flex-none px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                    >
                      {sendingEmail ? (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                          </svg>
                          Send Email
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Test Email Modal */}
        {showTestEmailModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl w-full max-w-md p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white">Send Test Email</h3>
                <button
                  onClick={() => setShowTestEmailModal(false)}
                  className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                    Recipient Email
                  </label>
                  <input
                    type="email"
                    value={testEmailAddress}
                    onChange={(e) => setTestEmailAddress(e.target.value)}
                    placeholder="test@example.com"
                    className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <p className="text-sm text-[#86868b]">
                  A test welcome email will be sent to this address.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowTestEmailModal(false)}
                    className="flex-1 px-4 py-3 border border-black/10 dark:border-white/10 rounded-xl font-medium hover:bg-black/5 dark:hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendTestEmail}
                    disabled={sendingEmail}
                    className="flex-1 px-4 py-3 bg-[#1d1d1f] dark:bg-white text-white dark:text-[#1d1d1f] rounded-xl font-medium hover:opacity-90 disabled:opacity-50"
                  >
                    {sendingEmail ? 'Sending...' : 'Send Test'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Campaign Modal */}
        {showCampaignModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-black/5 dark:border-white/5 flex items-center justify-between sticky top-0 bg-white dark:bg-[#1a1a1a]">
                <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white">New Email Campaign</h3>
                <button
                  onClick={() => setShowCampaignModal(false)}
                  className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    value={campaignData.name}
                    onChange={(e) => setCampaignData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., December Newsletter"
                    className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                    Subject Line *
                  </label>
                  <input
                    type="text"
                    value={campaignData.subject}
                    onChange={(e) => setCampaignData(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="e.g., Exciting updates from QuickMeet"
                    className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                    Audience
                  </label>
                  <select
                    value={campaignData.audience}
                    onChange={(e) => setCampaignData(prev => ({ ...prev, audience: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">All Users</option>
                    <option value="free">Free Users Only</option>
                    <option value="pro">Pro Users Only</option>
                    <option value="team">Team Users Only</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                    Email Content (HTML) *
                  </label>
                  <textarea
                    value={campaignData.html}
                    onChange={(e) => setCampaignData(prev => ({ ...prev, html: e.target.value }))}
                    placeholder="<div>Your email content here...</div>"
                    rows={8}
                    className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  />
                  <p className="text-xs text-[#86868b] mt-1">Use {"{{firstName}}"} for personalization</p>
                </div>
                <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    <strong>Note:</strong> Only users who have opted in to marketing emails will receive this campaign.
                  </p>
                </div>
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={() => setShowCampaignModal(false)}
                    className="flex-1 px-4 py-3 border border-black/10 dark:border-white/10 rounded-xl font-medium hover:bg-black/5 dark:hover:bg-white/5"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={sendCampaign}
                    disabled={sendingEmail}
                    className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 disabled:opacity-50"
                  >
                    {sendingEmail ? 'Sending...' : 'Send Campaign'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <div className="space-y-8">
            {/* Pricing Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-[#1d1d1f] dark:text-white">Pricing Configuration</h2>
                  <p className="text-sm text-[#86868b]">Update pricing and Stripe settings</p>
                </div>
                <button
                  onClick={savePricingConfig}
                  disabled={savingPricing || !pricingConfig}
                  className="px-4 py-2 bg-[#1d1d1f] dark:bg-white text-white dark:text-black text-sm font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {savingPricing ? 'Saving...' : 'Save'}
                </button>
              </div>

              {pricingLoading ? (
                <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-8 text-center border border-black/5 dark:border-white/5">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#1d1d1f] dark:border-white border-t-transparent mx-auto"></div>
                </div>
              ) : pricingConfig ? (
                <div className="grid lg:grid-cols-3 gap-4">
                  {/* Free */}
                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
                    <h3 className="font-semibold text-[#1d1d1f] dark:text-white mb-4">Free</h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-[#86868b] mb-1.5">Price Display</label>
                        <input type="text" value={pricingConfig.free.price} disabled className="w-full px-3 py-2 bg-black/5 dark:bg-white/5 rounded-lg text-[#1d1d1f] dark:text-white text-sm" />
                      </div>
                    </div>
                  </div>

                  {/* Pro */}
                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border-2 border-blue-500">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-[#1d1d1f] dark:text-white">Pro</h3>
                      <span className="text-xs text-blue-500 font-medium">ACTIVE</span>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-[#86868b] mb-1.5">Price</label>
                        <input
                          type="text"
                          value={pricingConfig.pro.price}
                          onChange={(e) => updatePricingField('pro', 'price', e.target.value)}
                          className="w-full px-3 py-2 bg-black/5 dark:bg-white/5 rounded-lg text-[#1d1d1f] dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-[#86868b] mb-1.5">Stripe Price ID</label>
                        <input
                          type="text"
                          value={pricingConfig.pro.stripePriceId || ''}
                          onChange={(e) => updatePricingField('pro', 'stripePriceId', e.target.value)}
                          placeholder="price_xxx"
                          className="w-full px-3 py-2 bg-black/5 dark:bg-white/5 rounded-lg text-[#1d1d1f] dark:text-white text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Team */}
                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border-2 border-purple-500">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-semibold text-[#1d1d1f] dark:text-white">Team</h3>
                      <span className="text-xs text-purple-500 font-medium">SEAT-BASED</span>
                    </div>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs text-[#86868b] mb-1.5">Seat Price</label>
                          <input
                            type="number"
                            value={pricingConfig.team.seatPrice || 20}
                            onChange={(e) => updatePricingField('team', 'seatPrice', parseInt(e.target.value) || 20)}
                            className="w-full px-3 py-2 bg-black/5 dark:bg-white/5 rounded-lg text-[#1d1d1f] dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-[#86868b] mb-1.5">Min Seats</label>
                          <input
                            type="number"
                            value={pricingConfig.team.minSeats || 5}
                            onChange={(e) => updatePricingField('team', 'minSeats', parseInt(e.target.value) || 5)}
                            className="w-full px-3 py-2 bg-black/5 dark:bg-white/5 rounded-lg text-[#1d1d1f] dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                          />
                        </div>
                      </div>
                      <div className="pt-3 border-t border-black/5 dark:border-white/5">
                        <div className="flex justify-between text-sm">
                          <span className="text-[#86868b]">Min Monthly</span>
                          <span className="font-medium text-[#1d1d1f] dark:text-white">${(pricingConfig.team.seatPrice || 20) * (pricingConfig.team.minSeats || 5)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>

            {/* Terms Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-[#1d1d1f] dark:text-white">Terms & Privacy</h2>
                  <p className="text-sm text-[#86868b]">Version management and acceptance stats</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={loadTermsPreview} className="px-4 py-2 bg-black/5 dark:bg-white/5 text-[#1d1d1f] dark:text-white text-sm font-medium rounded-xl hover:bg-black/10 dark:hover:bg-white/10 transition-colors">
                    Preview
                  </button>
                  <button onClick={() => setShowNewVersionModal(true)} className="px-4 py-2 bg-green-500/10 text-green-600 text-sm font-medium rounded-xl hover:bg-green-500/20 transition-colors">
                    New Version
                  </button>
                  <button
                    onClick={saveTermsConfig}
                    disabled={savingTerms || !termsConfig}
                    className="px-4 py-2 bg-[#1d1d1f] dark:bg-white text-white dark:text-black text-sm font-medium rounded-xl hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    {savingTerms ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>

              {!termsDataSeeded && (
                <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
                  <p className="text-sm font-medium text-red-600">Terms data not seeded</p>
                  <p className="text-xs text-red-500 mt-1">Users will see fallback content. Create versions to seed data.</p>
                </div>
              )}

              {termsLoading ? (
                <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-8 text-center border border-black/5 dark:border-white/5">
                  <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#1d1d1f] dark:border-white border-t-transparent mx-auto"></div>
                </div>
              ) : termsConfig ? (
                <div className="grid lg:grid-cols-3 gap-4">
                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 bg-blue-500/10 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <span className="font-medium text-[#1d1d1f] dark:text-white">Terms</span>
                    </div>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs text-[#86868b] mb-1.5">Required Version</label>
                        <input
                          type="text"
                          value={termsConfig.requiredVersion || '1.0.0'}
                          onChange={(e) => setTermsConfig(prev => ({ ...prev, requiredVersion: e.target.value }))}
                          className="w-full px-3 py-2 bg-black/5 dark:bg-white/5 rounded-lg text-[#1d1d1f] dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 bg-purple-500/10 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                      </div>
                      <span className="font-medium text-[#1d1d1f] dark:text-white">Privacy</span>
                    </div>
                    <div>
                      <label className="block text-xs text-[#86868b] mb-1.5">Version</label>
                      <input
                        type="text"
                        value={termsConfig.privacyVersion || '1.0.0'}
                        onChange={(e) => setTermsConfig(prev => ({ ...prev, privacyVersion: e.target.value }))}
                        className="w-full px-3 py-2 bg-black/5 dark:bg-white/5 rounded-lg text-[#1d1d1f] dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                      />
                    </div>
                  </div>

                  <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 border border-black/5 dark:border-white/5">
                    <div className="flex items-center gap-2 mb-4">
                      <div className="w-8 h-8 bg-green-500/10 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <span className="font-medium text-[#1d1d1f] dark:text-white">Acceptance</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-2xl font-bold text-[#1d1d1f] dark:text-white">{acceptanceStats.total}</div>
                        <div className="text-xs text-[#86868b]">Total</div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-green-500">{acceptanceStats.recent}</div>
                        <div className="text-xs text-[#86868b]">Last 30 days</div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {/* Version History */}
              {termsVersions.length > 0 && (
                <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl border border-black/5 dark:border-white/5 overflow-hidden">
                  <div className="px-6 py-4 border-b border-black/5 dark:border-white/5">
                    <h3 className="font-medium text-[#1d1d1f] dark:text-white">Version History</h3>
                  </div>
                  <div className="divide-y divide-black/5 dark:divide-white/5">
                    {termsVersions.map(version => (
                      <div key={version.id} className="px-6 py-4 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-0.5 rounded-lg text-xs font-medium ${
                            version.type === 'terms' ? 'bg-blue-500/10 text-blue-600' : 'bg-purple-500/10 text-purple-600'
                          }`}>
                            {version.type === 'terms' ? 'Terms' : 'Privacy'}
                          </span>
                          <span className="font-medium text-[#1d1d1f] dark:text-white">v{version.version}</span>
                          {version.title && <span className="text-sm text-[#86868b]">- {version.title}</span>}
                        </div>
                        <span className="text-xs text-[#86868b]">{version.createdAt?.toDate?.().toLocaleDateString() || ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Database Migrations Section */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-[#1d1d1f] dark:text-white">Database Migrations</h2>
                  <p className="text-sm text-[#86868b]">Run one-time database migrations for iOS compatibility</p>
                </div>
              </div>

              <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl border border-black/5 dark:border-white/5 overflow-hidden">
                <div className="p-6 space-y-4">
                  {/* Warning */}
                  <div className="p-4 bg-amber-500/10 rounded-xl border border-amber-500/20">
                    <div className="flex items-start gap-3">
                      <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-amber-600 dark:text-amber-500">One-Time Migration</p>
                        <p className="text-xs text-amber-600/80 dark:text-amber-500/80 mt-1">
                          This migration unifies the meeting schema for iOS compatibility. It converts isActive to status fields,
                          timestamps to seconds, and adds meetingCode fields. Safe to run multiple times (idempotent).
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Migration Info */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-black/5 dark:bg-white/5 rounded-xl">
                      <h4 className="font-medium text-[#1d1d1f] dark:text-white mb-2">What it does:</h4>
                      <ul className="text-sm text-[#86868b] space-y-1">
                        <li>• Migrates scheduledMeetings → meetings collection</li>
                        <li>• Adds meetingCode field to existing meetings</li>
                        <li>• Converts isActive boolean → status field</li>
                        <li>• Converts scheduledFor to seconds since epoch</li>
                      </ul>
                    </div>
                    <div className="p-4 bg-black/5 dark:bg-white/5 rounded-xl">
                      <h4 className="font-medium text-[#1d1d1f] dark:text-white mb-2">Status Values:</h4>
                      <ul className="text-sm text-[#86868b] space-y-1">
                        <li>• <span className="text-blue-500">scheduled</span> - Future meeting</li>
                        <li>• <span className="text-yellow-500">waiting</span> - Host joined, waiting to start</li>
                        <li>• <span className="text-green-500">active</span> - Meeting in progress</li>
                        <li>• <span className="text-gray-500">ended</span> / <span className="text-red-500">cancelled</span></li>
                      </ul>
                    </div>
                  </div>

                  {/* Run Button */}
                  <div className="flex items-center gap-4">
                    <button
                      onClick={runMigration}
                      disabled={migrationRunning}
                      className={`px-6 py-3 rounded-xl font-medium transition-all ${
                        migrationRunning
                          ? 'bg-gray-400 text-white cursor-not-allowed'
                          : 'bg-[#1d1d1f] dark:bg-white text-white dark:text-black hover:opacity-90'
                      }`}
                    >
                      {migrationRunning ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Running Migration...
                        </span>
                      ) : (
                        'Run Migration'
                      )}
                    </button>

                    {migrationResult && !migrationResult.error && (
                      <span className="text-sm text-green-500 flex items-center gap-1">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Completed
                      </span>
                    )}
                  </div>

                  {/* Migration Logs */}
                  {migrationLogs.length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-medium text-[#1d1d1f] dark:text-white mb-2">Migration Log:</h4>
                      <div className="bg-black dark:bg-black/50 rounded-xl p-4 font-mono text-xs text-green-400 max-h-48 overflow-y-auto">
                        {migrationLogs.map((log, index) => (
                          <div key={index} className="py-0.5">
                            <span className="text-gray-500">[{String(index + 1).padStart(2, '0')}]</span> {log}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Migration Results */}
                  {migrationResult && !migrationResult.error && (
                    <div className="mt-4 grid md:grid-cols-3 gap-4">
                      <div className="p-4 bg-green-500/10 rounded-xl text-center">
                        <div className="text-2xl font-bold text-green-500">{migrationResult.scheduledMeetings?.migrated || 0}</div>
                        <div className="text-xs text-[#86868b]">Scheduled Migrated</div>
                      </div>
                      <div className="p-4 bg-blue-500/10 rounded-xl text-center">
                        <div className="text-2xl font-bold text-blue-500">{migrationResult.existingMeetings?.updated || 0}</div>
                        <div className="text-xs text-[#86868b]">Existing Updated</div>
                      </div>
                      <div className="p-4 bg-red-500/10 rounded-xl text-center">
                        <div className="text-2xl font-bold text-red-500">{migrationResult.totalErrors || 0}</div>
                        <div className="text-xs text-[#86868b]">Errors</div>
                      </div>
                    </div>
                  )}

                  {/* Error Display */}
                  {migrationResult?.error && (
                    <div className="mt-4 p-4 bg-red-500/10 rounded-xl border border-red-500/20">
                      <p className="text-sm text-red-600 dark:text-red-400">
                        <strong>Error:</strong> {migrationResult.error}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deleteModalUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white">Manage User Account</h3>
              <button onClick={() => { setShowDeleteModal(false); setDeleteModalUser(null); }} className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-xl">
                <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              {/* Warning Icon */}
              <div className="flex justify-center">
                <div className="w-16 h-16 bg-red-500/10 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>

              {/* User Info */}
              <div className="p-4 bg-black/5 dark:bg-white/5 rounded-xl text-center">
                <div className="font-medium text-[#1d1d1f] dark:text-white">{deleteModalUser.displayName || 'No name'}</div>
                <div className="text-sm text-[#86868b]">{deleteModalUser.email}</div>
              </div>

              {/* Options */}
              <div className="space-y-3">
                {/* Disable Option */}
                <div className="p-4 bg-orange-500/10 rounded-xl border border-orange-500/20">
                  <h4 className="font-medium text-orange-600 dark:text-orange-400 mb-1">Disable Account</h4>
                  <p className="text-xs text-[#86868b] mb-3">
                    User cannot log in but account data is preserved. Can be restored later.
                  </p>
                  <button
                    onClick={() => deleteUser(deleteModalUser.id, 'disable')}
                    disabled={deletingUserId === deleteModalUser.id}
                    className="w-full px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 disabled:opacity-50 text-sm"
                  >
                    {deletingUserId === deleteModalUser.id ? 'Disabling...' : 'Disable Account'}
                  </button>
                </div>

                {/* Delete Option */}
                <div className="p-4 bg-red-500/10 rounded-xl border border-red-500/20">
                  <h4 className="font-medium text-red-600 dark:text-red-400 mb-1">Delete Account</h4>
                  <p className="text-xs text-[#86868b] mb-3">
                    Permanently deletes user and all their data. User can create a new account.
                  </p>
                  <button
                    onClick={() => deleteUser(deleteModalUser.id, 'delete')}
                    disabled={deletingUserId === deleteModalUser.id}
                    className="w-full px-4 py-2 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 disabled:opacity-50 text-sm"
                  >
                    {deletingUserId === deleteModalUser.id ? 'Deleting...' : 'Permanently Delete'}
                  </button>
                </div>
              </div>

              {/* Cancel */}
              <div className="pt-2">
                <button
                  onClick={() => { setShowDeleteModal(false); setDeleteModalUser(null); }}
                  className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 text-[#1d1d1f] dark:text-white rounded-xl font-medium hover:bg-black/10 dark:hover:bg-white/10"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Team Modal */}
      {showTeamModal && teamModalUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white">Assign Team Tier</h3>
              <button onClick={() => { setShowTeamModal(false); setTeamModalUser(null); }} className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-xl">
                <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div className="p-4 bg-black/5 dark:bg-white/5 rounded-xl">
                <div className="font-medium text-[#1d1d1f] dark:text-white">{teamModalUser.displayName || 'No name'}</div>
                <div className="text-sm text-[#86868b]">{teamModalUser.email}</div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">Seats</label>
                <input
                  type="number"
                  value={teamSeats}
                  onChange={(e) => setTeamSeats(Math.max(5, parseInt(e.target.value) || 5))}
                  min={5}
                  className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 rounded-xl text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500/50"
                />
              </div>
              <div className="p-4 bg-purple-500/10 rounded-xl">
                <div className="flex justify-between text-sm">
                  <span className="text-purple-600">Monthly</span>
                  <span className="font-bold text-purple-600">${teamSeats * (pricingConfig?.team?.seatPrice || 20)}/mo</span>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setShowTeamModal(false); setTeamModalUser(null); }} className="flex-1 px-4 py-3 bg-black/5 dark:bg-white/5 text-[#1d1d1f] dark:text-white rounded-xl font-medium hover:bg-black/10 dark:hover:bg-white/10">
                  Cancel
                </button>
                <button
                  onClick={() => updateUserTier(teamModalUser.id, 'team', teamSeats)}
                  disabled={updatingUserId === teamModalUser.id}
                  className="flex-1 px-4 py-3 bg-purple-500 text-white rounded-xl font-medium hover:bg-purple-600 disabled:opacity-50"
                >
                  {updatingUserId === teamModalUser.id ? 'Updating...' : 'Assign'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* New Version Modal */}
      {showNewVersionModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white">New Version</h3>
              <button onClick={() => { setShowNewVersionModal(false); setNewVersion({ version: '', type: 'terms', title: '', content: '', summary: '' }); }} className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-xl">
                <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex gap-2">
                {['terms', 'privacy'].map(type => (
                  <button
                    key={type}
                    onClick={() => setNewVersion(prev => ({ ...prev, type }))}
                    className={`flex-1 py-2 rounded-xl font-medium transition-colors ${
                      newVersion.type === type
                        ? type === 'terms' ? 'bg-blue-500 text-white' : 'bg-purple-500 text-white'
                        : 'bg-black/5 dark:bg-white/5 text-[#1d1d1f] dark:text-white'
                    }`}
                  >
                    {type === 'terms' ? 'Terms' : 'Privacy'}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">Version *</label>
                  <input
                    type="text"
                    value={newVersion.version}
                    onChange={(e) => setNewVersion(prev => ({ ...prev, version: e.target.value }))}
                    placeholder="1.1.0"
                    className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 rounded-xl text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">Title *</label>
                  <input
                    type="text"
                    value={newVersion.title}
                    onChange={(e) => setNewVersion(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="Terms of Service"
                    className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 rounded-xl text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">Summary</label>
                <input
                  type="text"
                  value={newVersion.summary}
                  onChange={(e) => setNewVersion(prev => ({ ...prev, summary: e.target.value }))}
                  placeholder="Brief description of changes"
                  className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 rounded-xl text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">Content *</label>
                <textarea
                  value={newVersion.content}
                  onChange={(e) => setNewVersion(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Full content (Markdown supported)"
                  rows={10}
                  className="w-full px-4 py-3 bg-black/5 dark:bg-white/5 rounded-xl text-[#1d1d1f] dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 resize-y font-mono text-sm"
                />
              </div>
              <div className="flex gap-3">
                <button onClick={() => { setShowNewVersionModal(false); setNewVersion({ version: '', type: 'terms', title: '', content: '', summary: '' }); }} className="flex-1 px-4 py-3 bg-black/5 dark:bg-white/5 text-[#1d1d1f] dark:text-white rounded-xl font-medium hover:bg-black/10 dark:hover:bg-white/10">
                  Cancel
                </button>
                <button
                  onClick={createNewVersion}
                  disabled={savingTerms || !newVersion.version || !newVersion.content || !newVersion.title}
                  className="flex-1 px-4 py-3 bg-green-500 text-white rounded-xl font-medium hover:bg-green-600 disabled:opacity-50"
                >
                  {savingTerms ? 'Creating...' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Terms Preview Modal */}
      {showTermsPreview && termsPreview && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl p-6 max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white">Terms Preview</h3>
                {termsPreview.usingFallback && <p className="text-xs text-yellow-600">Using fallback content</p>}
              </div>
              <button onClick={() => setShowTermsPreview(false)} className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-xl">
                <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-4">
              <div className="p-4 bg-black/5 dark:bg-white/5 rounded-xl">
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div><span className="text-[#86868b]">Terms:</span> <span className="font-medium text-[#1d1d1f] dark:text-white">v{termsPreview.config?.requiredVersion}</span></div>
                  <div><span className="text-[#86868b]">Privacy:</span> <span className="font-medium text-[#1d1d1f] dark:text-white">v{termsPreview.config?.privacyVersion}</span></div>
                  <div><span className="text-[#86868b]">Source:</span> <span className={`font-medium ${termsPreview.usingFallback ? 'text-yellow-600' : 'text-green-600'}`}>{termsPreview.usingFallback ? 'Fallback' : 'Firestore'}</span></div>
                </div>
              </div>
              <div className="rounded-xl border border-black/5 dark:border-white/5 overflow-hidden">
                <div className="px-4 py-3 bg-blue-500/10 border-b border-black/5 dark:border-white/5">
                  <span className="font-medium text-blue-600">Terms of Service</span>
                </div>
                <div className="p-4 max-h-48 overflow-y-auto">
                  <pre className="text-sm text-[#1d1d1f] dark:text-[#e5e5ea] whitespace-pre-wrap font-sans">{termsPreview.terms?.content || 'No content'}</pre>
                </div>
              </div>
              <div className="rounded-xl border border-black/5 dark:border-white/5 overflow-hidden">
                <div className="px-4 py-3 bg-purple-500/10 border-b border-black/5 dark:border-white/5">
                  <span className="font-medium text-purple-600">Privacy Policy</span>
                </div>
                <div className="p-4 max-h-48 overflow-y-auto">
                  <pre className="text-sm text-[#1d1d1f] dark:text-[#e5e5ea] whitespace-pre-wrap font-sans">{termsPreview.privacy?.content || 'No content'}</pre>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-black/5 dark:border-white/5">
              <button onClick={() => setShowTermsPreview(false)} className="px-4 py-2 bg-[#1d1d1f] dark:bg-white text-white dark:text-black rounded-xl font-medium hover:opacity-90">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* User Profile Modal */}
      {showProfileModal && profileModalUser && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-black/5 dark:border-white/5">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-full flex items-center justify-center text-white font-semibold text-xl ${
                  profileModalUser.disabled || profileModalUser.deleted ? 'bg-gradient-to-br from-red-400 to-red-500' :
                  profileModalUser.subscriptionTier === 'team' ? 'bg-gradient-to-br from-purple-500 to-purple-600' :
                  profileModalUser.subscriptionTier === 'pro' ? 'bg-gradient-to-br from-blue-500 to-blue-600' :
                  'bg-gradient-to-br from-gray-400 to-gray-500'
                }`}>
                  {(profileModalUser.displayName || profileModalUser.email || '?').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white">
                      {profileModalUser.displayName || 'No name'}
                    </h3>
                    {profileModalUser.isAdmin && (
                      <span className="px-2 py-0.5 bg-red-500/10 text-red-600 dark:text-red-400 text-xs font-semibold rounded-full">
                        ADMIN
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      profileModalUser.subscriptionTier === 'team' ? 'bg-purple-500 text-white' :
                      profileModalUser.subscriptionTier === 'pro' ? 'bg-blue-500 text-white' :
                      'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                    }`}>
                      {(profileModalUser.subscriptionTier || 'free').toUpperCase()}
                    </span>
                  </div>
                  <p className="text-sm text-[#86868b]">{profileModalUser.email}</p>
                </div>
              </div>
              <button
                onClick={() => { setShowProfileModal(false); setProfileModalUser(null); }}
                className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-xl"
              >
                <svg className="w-5 h-5 text-[#86868b]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Account Details */}
              <div>
                <h4 className="text-sm font-semibold text-[#1d1d1f] dark:text-white mb-3">Account Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-black/5 dark:bg-white/5 rounded-xl">
                    <p className="text-xs text-[#86868b] mb-1">User ID</p>
                    <p className="text-sm text-[#1d1d1f] dark:text-white font-mono truncate">{profileModalUser.id}</p>
                  </div>
                  <div className="p-3 bg-black/5 dark:bg-white/5 rounded-xl">
                    <p className="text-xs text-[#86868b] mb-1">Auth Provider</p>
                    <p className="text-sm text-[#1d1d1f] dark:text-white capitalize">
                      {profileModalUser.authProvider === 'google' ? 'Google' :
                       profileModalUser.authProvider === 'apple' ? 'Apple' : 'Email/Password'}
                    </p>
                  </div>
                  <div className="p-3 bg-black/5 dark:bg-white/5 rounded-xl">
                    <p className="text-xs text-[#86868b] mb-1">Created At</p>
                    <p className="text-sm text-[#1d1d1f] dark:text-white">
                      {profileModalUser.createdAt?.toDate?.()?.toLocaleString() || 'Unknown'}
                    </p>
                  </div>
                  <div className="p-3 bg-black/5 dark:bg-white/5 rounded-xl">
                    <p className="text-xs text-[#86868b] mb-1">Last Login</p>
                    <p className="text-sm text-[#1d1d1f] dark:text-white">
                      {profileModalUser.lastLoginAt?.toDate?.()?.toLocaleString() || 'Never'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Profile Information */}
              <div>
                <h4 className="text-sm font-semibold text-[#1d1d1f] dark:text-white mb-3">Profile Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 bg-black/5 dark:bg-white/5 rounded-xl">
                    <p className="text-xs text-[#86868b] mb-1">First Name</p>
                    <p className="text-sm text-[#1d1d1f] dark:text-white">{profileModalUser.firstName || '-'}</p>
                  </div>
                  <div className="p-3 bg-black/5 dark:bg-white/5 rounded-xl">
                    <p className="text-xs text-[#86868b] mb-1">Last Name</p>
                    <p className="text-sm text-[#1d1d1f] dark:text-white">{profileModalUser.lastName || '-'}</p>
                  </div>
                  <div className="p-3 bg-black/5 dark:bg-white/5 rounded-xl">
                    <p className="text-xs text-[#86868b] mb-1">Phone</p>
                    <p className="text-sm text-[#1d1d1f] dark:text-white">{profileModalUser.phone || '-'}</p>
                  </div>
                  <div className="p-3 bg-black/5 dark:bg-white/5 rounded-xl">
                    <p className="text-xs text-[#86868b] mb-1">Timezone</p>
                    <p className="text-sm text-[#1d1d1f] dark:text-white">{profileModalUser.timezone || '-'}</p>
                  </div>
                </div>
              </div>

              {/* Subscription Details */}
              <div>
                <h4 className="text-sm font-semibold text-[#1d1d1f] dark:text-white mb-3">Subscription</h4>
                <div className="p-4 rounded-xl border border-black/5 dark:border-white/5">
                  <div className="flex items-center justify-between mb-3">
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      profileModalUser.subscriptionTier === 'team' ? 'bg-purple-500/10 text-purple-600' :
                      profileModalUser.subscriptionTier === 'pro' ? 'bg-blue-500/10 text-blue-600' :
                      'bg-gray-500/10 text-gray-600'
                    }`}>
                      {(profileModalUser.subscriptionTier || 'free').toUpperCase()} Plan
                    </span>
                    <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                      profileModalUser.subscriptionStatus === 'active'
                        ? 'bg-green-500/10 text-green-600'
                        : 'bg-gray-500/10 text-gray-600'
                    }`}>
                      {profileModalUser.subscriptionStatus === 'active' ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    {profileModalUser.testMode && (
                      <div className="col-span-2 flex items-center gap-2 text-yellow-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <span>Test Mode - Set by {profileModalUser.testModeSetBy}</span>
                      </div>
                    )}
                    {profileModalUser.subscriptionTier === 'team' && (
                      <div className="col-span-2 flex items-center gap-2 text-purple-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span>{profileModalUser.teamSeatsUsed || 1} / {profileModalUser.teamSeats || 5} seats used</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Waitlist Status */}
              {profileModalUser.waitlistStatus && (
                <div>
                  <h4 className="text-sm font-semibold text-[#1d1d1f] dark:text-white mb-3">iOS Waitlist</h4>
                  <div className="p-4 rounded-xl bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/20">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        profileModalUser.waitlistStatus === 'approved'
                          ? 'bg-green-500/20 text-green-600'
                          : 'bg-amber-500/20 text-amber-600'
                      }`}>
                        {profileModalUser.waitlistStatus === 'approved' ? 'Approved' : 'Pending'}
                      </span>
                      <span className="text-sm text-[#86868b]">
                        {profileModalUser.useCase && `Use case: ${profileModalUser.useCase}`}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Onboarding Status */}
              <div>
                <h4 className="text-sm font-semibold text-[#1d1d1f] dark:text-white mb-3">Onboarding</h4>
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    profileModalUser.onboardingCompleted
                      ? 'bg-green-500/10 text-green-600'
                      : 'bg-amber-500/10 text-amber-600'
                  }`}>
                    {profileModalUser.onboardingCompleted ? 'Completed' : 'Not Completed'}
                  </span>
                  {profileModalUser.onboardingCompletedAt && (
                    <span className="text-sm text-[#86868b]">
                      Completed on {profileModalUser.onboardingCompletedAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="p-6 border-t border-black/5 dark:border-white/5 flex gap-3">
              <button
                onClick={() => { setShowProfileModal(false); setProfileModalUser(null); }}
                className="flex-1 px-4 py-3 bg-black/5 dark:bg-white/5 text-[#1d1d1f] dark:text-white rounded-xl font-medium hover:bg-black/10 dark:hover:bg-white/10"
              >
                Close
              </button>
              <button
                onClick={() => {
                  toggleAdminRole(profileModalUser.id, profileModalUser.isAdmin);
                  setProfileModalUser(prev => prev ? { ...prev, isAdmin: !prev.isAdmin } : null);
                }}
                disabled={profileModalUser.id === user?.uid}
                className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                  profileModalUser.isAdmin
                    ? 'bg-red-500 text-white hover:bg-red-600'
                    : 'bg-red-500/10 text-red-600 hover:bg-red-500/20'
                } disabled:opacity-40`}
              >
                {profileModalUser.isAdmin ? 'Revoke Admin' : 'Make Admin'}
              </button>
              <button
                onClick={() => handleDeleteClick(profileModalUser.id)}
                className="px-4 py-3 bg-red-500 text-white rounded-xl font-medium hover:bg-red-600"
              >
                {profileModalUser.disabled ? 'Restore' : 'Disable'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Template Editor Modal */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#1a1a1a] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-black/5 dark:border-white/5 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-[#1d1d1f] dark:text-white">
                {editingTemplate.isNew ? 'Create New Template' : `Edit Template: ${editingTemplate.name || editingTemplate.id}`}
              </h3>
              <button
                onClick={() => setEditingTemplate(null)}
                className="p-2 hover:bg-black/5 dark:hover:bg-white/5 rounded-lg"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {/* Template ID */}
              <div>
                <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                  Template ID *
                </label>
                <input
                  type="text"
                  value={editingTemplate.id}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') }))}
                  placeholder="my_template_id"
                  disabled={!editingTemplate.isNew && !editingTemplate.isDefault}
                  className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                />
                <p className="text-xs text-[#86868b] mt-1">Lowercase letters, numbers, underscores only</p>
              </div>

              {/* Template Name */}
              <div>
                <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Welcome Email"
                  className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Subject & Category Row */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                    Email Subject *
                  </label>
                  <input
                    type="text"
                    value={editingTemplate.subject}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Welcome to QuickMeet!"
                    className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                    Category
                  </label>
                  <select
                    value={editingTemplate.category || 'marketing'}
                    onChange={(e) => setEditingTemplate(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="transactional">Transactional</option>
                    <option value="marketing">Marketing</option>
                    <option value="onboarding">Onboarding</option>
                    <option value="feedback">Feedback</option>
                    <option value="notification">Notification</option>
                  </select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                  Description
                </label>
                <input
                  type="text"
                  value={editingTemplate.description || ''}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="A brief description of when this template is used"
                  className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* HTML Content */}
              <div>
                <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                  HTML Content *
                </label>
                <p className="text-xs text-[#86868b] mb-2">
                  Use {'{{firstName}}'} for personalization. Inline styles recommended for email compatibility.
                </p>
                <textarea
                  value={editingTemplate.html || ''}
                  onChange={(e) => setEditingTemplate(prev => ({ ...prev, html: e.target.value }))}
                  rows={12}
                  placeholder="<div>Your HTML email content...</div>"
                  className="w-full px-4 py-3 rounded-xl border border-black/10 dark:border-white/10 bg-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                />
              </div>

              {/* Preview */}
              <div>
                <label className="block text-sm font-medium text-[#1d1d1f] dark:text-white mb-2">
                  Preview
                </label>
                <div className="border border-black/10 dark:border-white/10 rounded-xl p-4 bg-white dark:bg-white overflow-auto max-h-64">
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize((editingTemplate.html || '').replace(/\{\{firstName\}\}/g, 'John')) }} />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="px-6 py-4 border-t border-black/5 dark:border-white/5 flex gap-3">
              <button
                onClick={() => setEditingTemplate(null)}
                className="flex-1 px-4 py-3 border border-black/10 dark:border-white/10 rounded-xl font-medium hover:bg-black/5 dark:hover:bg-white/5"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!editingTemplate.id || !editingTemplate.name || !editingTemplate.subject || !editingTemplate.html) {
                    toast.error('Please fill in all required fields');
                    return;
                  }
                  await saveTemplate({
                    id: editingTemplate.id,
                    name: editingTemplate.name,
                    subject: editingTemplate.subject,
                    description: editingTemplate.description || '',
                    category: editingTemplate.category || 'marketing',
                    html: editingTemplate.html
                  });
                  setEditingTemplate(null);
                }}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
              >
                {editingTemplate.isNew ? 'Create Template' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
